import { useState, useCallback, useEffect, useRef } from "react";
import { FeatureFlags, PatientRecord, RecordingState, TranscriptSectionStatus } from "../utils/types";
import AudioDb from "../classes/audioDb";
import { v4 as uuidv4 } from "uuid";
import * as Sentry from "@sentry/react";
import { toast } from "react-toastify";
import { useAppDispatch } from "@/common/hooks/useRedux";
import { finalizingSettled, finalizingStarted, setGlobalRecordingState } from "@/state/redux/globalState";
import { updateTranscriptSectionStatus } from "@/state/redux/patientTableRecordsSlice";
import { useDataContextApi } from "@/state/providers/DataProvider";
import { fetchPatientRecord, upsertTranscriptSection } from "../lib/supabaseClient";
import { markRecordingDiscarded, setRecordingVisit } from "../lib/recordingLease";
import { useAccountContext } from "@/state/providers/AccountProvider";
import { useChromeExtension, type AudioCaptureConfig } from "@/state/providers/ChromeExtensionProvider";
import { isTrustedAudioChunkMessage } from "./audioChunkMessageGuard";
import { createAudioChunkAccumulator } from "../lib/audioChunkAccumulator";
import { firstBytesHex, validateWebmHeader, WEBM_HEADER_SCAN_BYTES } from "../lib/webmHeader";
import { verifyAudioBlobIntegrity, buildDeadBlobSentryExtra } from "../lib/audioBlobIntegrity";
import {
    resolveRecorderTimeslice,
    createRecordingLivenessResponder,
    SNAPSHOT_HEARTBEAT_MS,
    STALE_OWNER_MS,
} from "../lib/recorderPersistence";
import { announceSessionEnded, createSessionResponder, type HeldSession } from "../lib/crossTabSession";
import {
    buildRecordingErrorExtra,
    captureActiveMicAndSettings,
    captureAudioInputDevices,
    captureSessionEnvironment,
    setAudioContextSampleRate,
} from "../lib/recordingTelemetry";
import { openMicStream, resolveStartAudioConstraints } from "@/features/recording-widget/mic/micStart";
import { MicStartError } from "@/features/recording-widget/mic/micStatus";

export const isRecordingError = (recordingState: RecordingState) => {
    return (
        recordingState === RecordingState.error ||
        recordingState === RecordingState.no_device ||
        recordingState === RecordingState.no_permission
    );
};

// EN-1609: how long a cross-tab stop-and-save waits for the final audio hand-off before replying
// ok:false. Below the requester's 15s requestStopAndSave timeout so the reply arrives first.
const SESSION_HANDOFF_TIMEOUT_MS = 10_000;

export const isRecording = (recordingState: RecordingState) => {
    return recordingState === RecordingState.recording || recordingState === RecordingState.paused;
};

/**
 * Checks if the file size is valid
 */
export function isFileSizeValid(size: number): boolean {
    // 600mb
    if (size > 600_000_000) {
        return false;
    }
    return true;
}

export interface recorderControls {
    startRecording: (
        patientRecord: PatientRecord,
        transcriptSectionId: string,
        onAudioDataAvailable: (
            audioFile: File,
            patientRecord: PatientRecord,
            transcriptSectionId: string,
            chunkIndex: number,
            end: boolean,
        ) => Promise<void>,
    ) => any;
    // discard: a start the vet cancelled. The capture is stopped and released without saving or uploading
    // (browser and extension transports).
    stopRecording: (options?: { discard?: boolean }) => Promise<void>;
    togglePauseResume: () => Promise<void>;
    isRecording: boolean;
    recordingState: RecordingState;
    recordingTime: number;
    audioStream?: MediaStream;
    activeRecordingId: string;
    setActiveRecordingId: React.Dispatch<React.SetStateAction<string>>;
    resetRecorder: (force?: boolean) => Promise<void>;
    setRecordingState: React.Dispatch<React.SetStateAction<RecordingState>>;
    recordingMessage: { message: string; type: "info" | "error" | "warning" } | null;
    setRecordingMessage: React.Dispatch<
        React.SetStateAction<{ message: string; type: "info" | "error" | "warning" } | null>
    >;
    chunkBuffer: React.MutableRefObject<AudioChunk[]>;
    blobIndexRef: React.MutableRefObject<number>;
    pauseResumeSupported: boolean;
    // True when a start would go (or went) through the Chrome extension instead of getUserMedia.
    isExtensionTransport: boolean;
}

// Legacy wording for a mic that would not open, keyed by DOMException name. The hourly
// rollover still shows it; the record press classifies the MicStartError itself.
const legacyMicStartMessage = (name: string): string =>
    name === "NotAllowedError"
        ? "Please allow microphone access."
        : name === "NotFoundError"
          ? "Microphone not found. Check settings."
          : "Error setting up microphone.";

export type MediaAudioTrackConstraints = Pick<
    MediaTrackConstraints,
    | "deviceId"
    | "groupId"
    | "autoGainControl"
    | "channelCount"
    | "echoCancellation"
    | "noiseSuppression"
    | "sampleRate"
    | "sampleSize"
>;

export interface AudioChunk {
    transcriptSectionId: string;
    transcriptionId: string;
    patientRecordId: string;
    chunkIndex: number;
    success: boolean;
    uploading: boolean;
    data: Blob;
    retryCount: number;
    retryTimeoutId?: NodeJS.Timeout;
}

function assembleChunksToBlob(chunks: string[], type: string | undefined): Blob {
    const blobParts = chunks.map((dataUrl) => {
        const base64 = dataUrl.startsWith("data:") ? dataUrl.split(",")[1] : dataUrl;
        const byteChars = atob(base64);
        const byteNums = new Uint8Array(byteChars.length);
        for (let i = 0; i < byteChars.length; i++) {
            byteNums[i] = byteChars.charCodeAt(i);
        }
        return byteNums;
    });
    return new Blob(blobParts, { type });
}

/** Sidepanel bridge: chunks from postMessage; content-script path: use stopResult.audioData when chunks are empty. */
function blobFromStreamedChunksOrStopResult(
    streamedChunks: string[],
    stopResult: { audioData?: string } | null | undefined,
    mimeType: string | undefined,
): Blob | null {
    if (streamedChunks.length > 0) {
        return assembleChunksToBlob(streamedChunks, mimeType);
    }
    if (stopResult?.audioData) {
        return assembleChunksToBlob([stopResult.audioData], mimeType);
    }
    return null;
}

/** EN-1609: an extension capture with no live owner (paused, or still running after its panel closed), which a start stopped and handed back. */
interface ReplacedPausedSession {
    data: string | null; // base64 data URL, as STOP_AUDIO_CAPTURE's includeData returns
    visitId: string | null; // null when an older web build started the session
    transcriptSectionId: string | null;
}

/** What an extension start responds with (all optional: older extensions send less). */
interface ExtensionStartResult {
    success?: boolean;
    error?: string;
    replacedPausedSession?: ReplacedPausedSession;
    orphanTooLarge?: boolean;
}

/** Owner nonce no live tab has, so recovery treats the replaced session's row as abandoned. */
const ORPHANED_EXTENSION_OWNER = "orphaned-extension";

interface useAudioRecorderProps {
    audioTrackConstraints?: MediaAudioTrackConstraints;
    mediaRecorderOptions?: MediaRecorderOptions;
    // The Monitoring document frozen at recording-start (see RealtimeProvider's
    // recordingMonitoringDocumentIdRef / captureMonitoringDocumentIdForRecording), threaded in by
    // the caller so it can be persisted alongside each IndexedDB write below — this hook has no
    // context access of its own. Optional so existing/test callers that don't pass it keep working;
    // absent, persisted rows simply carry no Monitoring target.
    recordingMonitoringDocumentIdRef?: React.MutableRefObject<string | null>;
    // EN-1609: how a cross-tab stop-and-save stops this tab's session, so the caller can update its own
    // UI (widget state, visit fields) the way a user-initiated finish does. Must reject when the save
    // fails. Absent, the responder calls stopRecording directly.
    crossTabStopRef?: React.MutableRefObject<(() => Promise<void>) | null>;
}
/**
 * @returns Controls for the recording. Details of returned controls are given below
 *
 * @param `audioTrackConstraints`: Takes a {@link https://developer.mozilla.org/en-US/docs/Web/API/MediaTrackSettings#instance_properties_of_audio_tracks subset} of `MediaTrackConstraints` that apply to the audio track
 * @param `mediaRecorderOptions`: A {@link https://developer.mozilla.org/en-US/docs/Web/API/MediaRecorder/MediaRecorder MediaRecorderOptions} object that is passed to the MediaRecorder constructor
 *
 * @details `startRecording`: Calling this method would result in the recording to start. Sets `isRecording` to true
 * @details `stopRecording`: This results in a recording in progress being stopped and the resulting audio being present in `recordingBlob`. Sets `isRecording` to false
 * @details `togglePauseResume`: Calling this method would pause the recording if it is currently running or resume if it is paused. Toggles the value `isPaused`
 * @details `recordingState`: This is the current state of the recording. It can be one of `recording`, `paused`, `inactive` or `error`
 * @details `recordingTime`: Number of seconds that the recording has gone on. This is updated every second
 */
const useAudioRecorder = ({
    audioTrackConstraints,
    mediaRecorderOptions,
    recordingMonitoringDocumentIdRef,
    crossTabStopRef,
}: useAudioRecorderProps = {}): recorderControls => {
    const localAudioDb = AudioDb.getInstance();
    const dispatch = useAppDispatch();
    const [recordingState, setRecordingState] = useState<RecordingState>(RecordingState.inactive);
    const [recordingTime, setRecordingTime] = useState(0);
    const [mediaRecorder, setMediaRecorder] = useState<MediaRecorder>();
    // The live browser recorder and its AudioContext, set and cleared with `mediaRecorder`. A ref, so a
    // stopRecording closure captured before the start (the provider's cancel branch) still reaches them.
    const liveBrowserRecorderRef = useRef<{ recorder: MediaRecorder; audioContext: AudioContext } | null>(null);
    const [audioStream, setAudioStream] = useState<MediaStream>();
    const [mimeType, setMimeType] = useState(mediaRecorderOptions ? mediaRecorderOptions.mimeType : "audio/wav");
    const [activeRecordingId, setActiveRecordingId] = useState<string>("");
    const [recordingMessage, setRecordingMessage] = useState<{
        message: string;
        type: "info" | "error" | "warning";
    } | null>(null);
    const timerIntervalRef = useRef<NodeJS.Timeout | null>(null);
    const hourIntervalRef = useRef<NodeJS.Timeout | undefined>(undefined);
    const patientRecordRef = useRef<PatientRecord | null>(null);
    const transcriptSectionIdRef = useRef<string | null>(null);
    const onAudioDataAvailableRef = useRef<any>(null);
    const hourCounterRef = useRef<number>(0);
    // EN-1499: the audio constraints the session's first start resolved. The hourly rollover reuses
    // them, so the next hour records on the same mic with no re-enumeration gap.
    const sessionAudioConstraintsRef = useRef<true | MediaTrackConstraints | null>(null);

    // Locks the transport (extension vs browser) at the moment startRecording commits to a
    // path. All stop/cleanup paths read this ref instead of re-evaluating isExtensionActive,
    // which is reactive and can flip from false→true mid-session if the provider initialises
    // after the user clicks record. Without this lock, stopRecording/stopErroredRecording/
    // resetRecorder/hourly-restart would take the extension path even though the recording
    // started via getUserMedia, skipping mediaRecorder.stop() and leaving the mic stream open.
    const activeTransportRef = useRef<"extension" | "browser" | null>(null);

    // Guards against concurrent stopRecording invocations (e.g. user double-tapping stop, or
    // a watchdog firing while a manual stop is mid-await). Without this, the second call
    // would proceed in parallel — disarming watchdogs the first call already disarmed,
    // snapshotting an already-snapshotted-and-cleared accumulator, and potentially racing
    // to upload an empty blob (EN-719 review follow-up).
    const isStoppingRef = useRef(false);

    // Browser-transport session generation. A fresh start bumps it, the hourly rollover reuses it
    // (same session), and every stop bumps it, so a start still waiting on getUserMedia can tell
    // it was superseded and must not touch the session (see the check in startRecording).
    const browserStartGenerationRef = useRef(0);

    const { getIncompleteUploads } = useDataContextApi();
    const { containsFeatureFlag, accountData } = useAccountContext();

    // chunk buffer holds the current recording chunks in mem
    const chunkBuffer = useRef<AudioChunk[]>([]);
    const blobIndexRef = useRef(0);

    // Accumulates base64-encoded audio chunk data streamed via AUDIO_CHUNK_AVAILABLE
    // postMessage events from the sidepanel bridge during active recording. Gated by
    // arm/disarm so late chunks from a prior recording's tail can't leak into the
    // next recording's blob (EN-719).
    const chunkAccumulatorRef = useRef(createAudioChunkAccumulator());

    // Silence watchdog for the extension path: fires after 3 s of no AUDIO_CHUNK_AVAILABLE
    // messages, showing the "No audio detected" warning. The browser path has no silence warning.
    const silenceWatchdogRef = useRef<NodeJS.Timeout | null>(null);

    // When false, the postMessage listener must not arm/restart the silence watchdog (e.g. after
    // stopRecording clears the timer but before/while stopAudioCapture awaits — late chunks otherwise
    // reschedule a spurious warning).
    const extensionSilenceWatchdogArmedRef = useRef(false);

    // When true, extension silence watchdog must not call setRecordingMessage for "No audio detected."
    // Preserves hourly rollover segment-save errors from being overwritten while recording continues.
    const suppressExtensionSilenceWarningRef = useRef(false);

    const restartExtensionSilenceWatchdog = () => {
        if (silenceWatchdogRef.current) clearTimeout(silenceWatchdogRef.current);
        silenceWatchdogRef.current = setTimeout(() => {
            if (!extensionSilenceWatchdogArmedRef.current) {
                return;
            }
            if (suppressExtensionSilenceWarningRef.current) {
                return;
            }
            setRecordingMessage({ message: "No audio detected.", type: "warning" });
        }, 3000);
    };

    // EN-836: the timeslice MediaRecorder is started with. Always finite (see
    // resolveRecorderTimeslice) so ondataavailable fires periodically and we can persist the
    // in-progress recording to IndexedDB — surviving a mid-recording reload/crash. This does
    // NOT enable chunk UPLOAD; partial chunks are never sent to the backend, only the assembled
    // complete file is uploaded at stop.
    const recorderTimeslice = resolveRecorderTimeslice(containsFeatureFlag(FeatureFlags.CHUNK_UPLOAD));

    // Answer cross-tab recording-liveness pings while this tab is recording, so another tab's
    // upload-recovery skips this in-progress recording's partial IndexedDB snapshots (EN-652).
    const recordingStateRef = useRef(recordingState);
    recordingStateRef.current = recordingState;
    useEffect(() => {
        return createRecordingLivenessResponder(() => isRecording(recordingStateRef.current));
    }, []);

    // EN-1609: answer other tabs' session probes, and stop-and-save on their request, so a second
    // tab can save this tab's (possibly paused) recording instead of stranding it. Reads refs, not
    // state, because the responder is installed once. stopRecordingRef is assigned once
    // stopRecording is defined below.
    const stopRecordingRef = useRef<(() => Promise<void>) | null>(null);
    const sessionStopRef = useRef<Promise<void> | null>(null);
    // Waiters for a session's final-audio hand-off, keyed by transcript section id. Settled by the
    // browser terminal `ondataavailable` (after its upload/persist hand-off) or by stopRecording's
    // extension branch (which hands off inline); `ok` is false when that hand-off failed.
    const handoffWaitersRef = useRef(new Map<string, (ok: boolean) => void>());
    const settleHandoff = (sectionId: string | null, ok: boolean) => {
        if (!sectionId) return;
        handoffWaitersRef.current.get(sectionId)?.(ok);
        handoffWaitersRef.current.delete(sectionId);
    };
    useEffect(() => {
        const getHeldSession = (): Omit<HeldSession, "nonce"> | null => {
            const state = recordingStateRef.current;
            const record = patientRecordRef.current;
            // A session that is already stopping is not offered to other tabs.
            if (sessionStopRef.current || isStoppingRef.current) return null;
            if (!record || !isRecording(state)) return null;
            const transport = activeTransportRef.current;
            return {
                state: state === RecordingState.paused ? "paused" : "recording",
                visitId: record.id,
                patientName: record.patient_name ?? null,
                ...(transport ? { transport } : {}),
            };
        };
        // Resolves once the section's final audio is handed to the upload/persistence path; rejects
        // if that hand-off failed or did not happen within SESSION_HANDOFF_TIMEOUT_MS.
        const waitForHandoff = (sectionId: string): { done: Promise<void>; cancel: () => void } => {
            let timer: ReturnType<typeof setTimeout> | undefined;
            const done = new Promise<void>((resolve, reject) => {
                handoffWaitersRef.current.set(sectionId, (ok) => {
                    clearTimeout(timer);
                    if (ok) resolve();
                    else reject(new Error("Final audio hand-off failed"));
                });
                timer = setTimeout(() => {
                    handoffWaitersRef.current.delete(sectionId);
                    reject(new Error("Final audio hand-off timed out"));
                }, SESSION_HANDOFF_TIMEOUT_MS);
            });
            const cancel = () => {
                clearTimeout(timer);
                handoffWaitersRef.current.delete(sectionId);
            };
            return { done, cancel };
        };
        // Resolves only after the stop ran AND the final audio was handed off (the browser path's
        // terminal chunk arrives on a later macrotask than stopRecording's return). Idempotent: a
        // request arriving mid-stop awaits the in-flight stop, including a user stop already in
        // flight, and one arriving after the session ended resolves without stopping again.
        // Promise.resolve().then turns a synchronous throw into a rejection (replied as ok:false).
        const stopAndSave = (): Promise<void> => {
            if (sessionStopRef.current) return sessionStopRef.current;
            const sectionId = transcriptSectionIdRef.current;
            const userStopInFlight = isStoppingRef.current;
            if (!sectionId || (!userStopInFlight && !getHeldSession())) return Promise.resolve();
            // Registered before stopping so a hand-off that settles during the stop is not missed.
            const handoff = waitForHandoff(sectionId);
            const stop = Promise.all([
                // A user stop in flight makes stopRecording return early; the hand-off still settles.
                // Only a stop this request starts goes through the caller's crossTabStopRef.
                Promise.resolve().then(() =>
                    (userStopInFlight
                        ? stopRecordingRef.current
                        : (crossTabStopRef?.current ?? stopRecordingRef.current))?.(),
                ),
                handoff.done,
            ])
                .then(() => undefined)
                .finally(() => {
                    handoff.cancel();
                    sessionStopRef.current = null;
                });
            sessionStopRef.current = stop;
            return stop;
        };
        const removeResponder = createSessionResponder(getHeldSession, stopAndSave);
        const handlePageHide = () => {
            if (getHeldSession()) announceSessionEnded();
        };
        window.addEventListener("pagehide", handlePageHide);
        return () => {
            removeResponder();
            window.removeEventListener("pagehide", handlePageHide);
        };
    }, []);

    // EN-1609: a paused MediaRecorder emits no data, so no snapshot write re-stamps the row's
    // snapshotAt. Heartbeat it while paused so other tabs' recovery doesn't take a long pause for an
    // abandoned recording (STALE_OWNER_MS). Browser transport only: that is the path whose
    // ondataavailable writes the mid-recording snapshot rows. Runs until resume, stop, or unmount.
    useEffect(() => {
        if (recordingState !== RecordingState.paused || activeTransportRef.current !== "browser") return;
        const transcriptId = patientRecordRef.current?.transcriptions?.id;
        const sectionId = transcriptSectionIdRef.current;
        if (!transcriptId || !sectionId) return;
        const heartbeat = setInterval(() => {
            void localAudioDb.touchSnapshot(transcriptId, sectionId);
        }, SNAPSHOT_HEARTBEAT_MS);
        return () => clearInterval(heartbeat);
    }, [recordingState, localAudioDb]);

    // set mimeType based on supported options in browser on mount
    useEffect(() => {
        const options = ["audio/webm", "audio/mp3", "audio/wav", "audio/mp4"];
        const chosenOption = options.find((option) => MediaRecorder.isTypeSupported(option));
        if (!chosenOption) {
            Sentry.addBreadcrumb({
                message: "Browser does not support audio recording",
                level: "error",
            });
            setRecordingMessage({ message: "This browser does not support the correct audio formats.", type: "error" });
            setRecordingState(RecordingState.no_device);
            dispatch(setGlobalRecordingState(RecordingState.no_device));
            return;
        }

        setMimeType(chosenOption);
    }, []);

    const hasMicrophonePermission = useCallback(() => {
        return navigator.permissions.query({ name: "microphone" }).then((permissionStatus) => {
            return permissionStatus.state === "granted";
        });
    }, []);

    // handle refresh or tab close while recording or paused to stop recording
    useEffect(() => {
        /**
         * Handles the `beforeunload` event to manage the state of the audio recorder.
         * If the recording is in progress or paused, it stops the media stream to prevent
         * any issues on page refresh.
         *
         * @param event - The `beforeunload` event object.
         */
        const handleBeforeUnload = (event: any) => {
            if (recordingState === RecordingState.recording || recordingState === RecordingState.paused) {
                // stop stream no matter what on refresh. Comment if we want to allow cancel of refresh while recording
                //mediaRecorder?.stream.getAudioTracks().forEach((t) => t.stop());

                event.preventDefault();

                // onload cancel event do nothing
                if (event?.cancelable) {
                    return;
                }
                mediaRecorder?.stream.getAudioTracks().forEach((t) => t.stop());

                event.returnValue = "";
            }
        };

        const isOnIOS = navigator?.userAgent?.match(/iPad/i) || navigator.userAgent.match(/iPhone/i);
        const eventName = isOnIOS ? "pagehide" : "beforeunload";
        window.addEventListener(eventName, handleBeforeUnload);

        // Cleanup function
        return () => {
            window.removeEventListener(eventName, handleBeforeUnload);
        };
    }, [recordingState]);

    const _startTimer: () => void = useCallback(() => {
        const interval = setInterval(() => {
            setRecordingTime((time) => time + 1);
        }, 1000);
        timerIntervalRef.current = interval;
    }, [setRecordingTime]);

    const _stopTimer: () => void = useCallback(() => {
        if (timerIntervalRef.current != null) {
            clearInterval(timerIntervalRef.current as any);
            timerIntervalRef.current = null;
        }
    }, [timerIntervalRef]);

    const _handleIncomplete = (patientRecord: PatientRecord) => {
        // check for any !success chunks
        if (chunkBuffer.current.some((chunk) => !chunk.success)) {
            // make into 1 file
            const file = createAudioFromBlobs(chunkBuffer.current.map((chunk) => chunk.data));

            const transcriptSectionId = chunkBuffer.current[0].transcriptSectionId;

            // write the whole audio to indexdb for classic incomplete flow
            localAudioDb.addTranscriptSectionAudio(
                patientRecord.transcriptions?.id as string,
                transcriptSectionId,
                file,
                file.name,
                0,
                patientRecord.id as string,
                patientRecord.patient_name as string,
                patientRecord.doctors?.id as string,
                undefined,
                recordingMonitoringDocumentIdRef?.current ?? null,
                accountDataRef.current?.id ?? null,
            );

            // set as incomplete in redux
            dispatch(
                updateTranscriptSectionStatus({
                    id: patientRecord.id,
                    sectionId: transcriptSectionId,
                    status: TranscriptSectionStatus.FailedIncompleteUpload,
                }),
            );

            // refresh incomplete
            getIncompleteUploads();

            // update db
            // account_id read through accountDataRef (not accountData): _handleIncomplete is
            // reached via long-lived onmute/onended closures bound at recording start, so a
            // direct closure read would use stale-at-record-start data (see the accountDataRef
            // rationale below, EN-719).
            upsertTranscriptSection({
                id: transcriptSectionId,
                data: "",
                transcription_id: patientRecord.transcriptions?.id as string,
                status: TranscriptSectionStatus.FailedIncompleteUpload,
                patient_record_id: patientRecord.id,
                account_id: accountDataRef.current?.id,
            });
        }

        // CLEANUP
        chunkBuffer.current.forEach((chunk) => {
            if (chunk.retryTimeoutId) {
                clearTimeout(chunk.retryTimeoutId);
                chunk.retryTimeoutId = undefined;
            }
        });

        chunkBuffer.current = [];
        blobIndexRef.current = 0;
    };

    // Feature-probe the audio-capture methods rather than trusting `isAvailable` alone.
    // Older installed extensions may inject `window.chromeExtension` with `isAvailable: true`
    // but without pauseAudioCapture / resumeAudioCapture. In the sidebar the EXTENSION_METADATA
    // handler in ChromeExtensionProvider overwrites window.chromeExtension with the sendToParent
    // API (which has all methods), but page-api.js from the old extension loads first. Including
    // pauseAudioCapture / resumeAudioCapture in the probe ensures the old API is rejected and
    // the browser path (or the correct sidebar sendToParent API once EXTENSION_METADATA arrives)
    // is used. This also prevents "api.pauseAudioCapture is not a function" TypeErrors and the
    // unreliable content-script → background path that fails when the MV3 service worker is idle.
    const isExtension =
        typeof window !== "undefined" &&
        !!window.chromeExtension &&
        window.chromeExtension.isAvailable === true &&
        typeof window.chromeExtension.startAudioCapture === "function" &&
        typeof window.chromeExtension.stopAudioCapture === "function" &&
        typeof window.chromeExtension.closeAudioCapture === "function" &&
        typeof window.chromeExtension.pauseAudioCapture === "function" &&
        typeof window.chromeExtension.resumeAudioCapture === "function";
    const {
        isAvailable: isExtensionAvailableInProvider,
        extensionId,
        version: extensionVersion,
        isSidebarWindow,
        startAudioCapture,
        stopAudioCapture,
        pauseAudioCapture,
        resumeAudioCapture,
        closeAudioCapture,
    } = useChromeExtension();

    // Refs so the AUDIO_CHUNK_AVAILABLE message handler (registered once) always sees current values.
    const extensionIdRef = useRef(extensionId);
    const isSidebarWindowRef = useRef(isSidebarWindow);
    useEffect(() => {
        extensionIdRef.current = extensionId;
    }, [extensionId]);
    useEffect(() => {
        isSidebarWindowRef.current = isSidebarWindow;
    }, [isSidebarWindow]);

    // Same pattern for recoverIfPrefixed's Sentry diagnostics: the function is re-created on
    // each render and closes over these values, but a recording-in-progress may straddle
    // AccountProvider refreshes (subscription updates, feature flag fetches) or extension
    // install/uninstall heartbeats. Reading through refs guarantees the Sentry event tags
    // reflect state at invocation time, not at the render that bound the closure (EN-719).
    const accountDataRef = useRef(accountData);
    const isExtensionAvailableInProviderRef = useRef(isExtensionAvailableInProvider);
    useEffect(() => {
        accountDataRef.current = accountData;
    }, [accountData]);
    useEffect(() => {
        isExtensionAvailableInProviderRef.current = isExtensionAvailableInProvider;
    }, [isExtensionAvailableInProvider]);

    // Breadcrumb-only refs: `togglePauseResume` is a stable useCallback whose
    // closure binds `recordingTime`/`activeRecordingId` from the render that
    // last recreated it (when `mediaRecorder` / `recordingState` changed). Adding
    // them to the dependency array would recreate the callback every second
    // (recordingTime ticks). Refs give the breadcrumb fresh values without
    // destabilising the callback identity. Note: refs are updated via the
    // effects below, which run AFTER render — so when a synchronous click
    // fires togglePauseResume between a tick and its render, the breadcrumb
    // reports a `recordingTime` that's at most one render cycle (~1s) behind.
    // That's accurate enough for forensic correlation; perfect precision
    // would require either dropping useCallback memoisation or polling Date.now()
    // at breadcrumb time, both worse trade-offs.
    const recordingTimeRef = useRef(0);
    const activeRecordingIdRef = useRef("");
    useEffect(() => {
        recordingTimeRef.current = recordingTime;
    }, [recordingTime]);
    useEffect(() => {
        activeRecordingIdRef.current = activeRecordingId;
    }, [activeRecordingId]);

    // Guard all three conditions: window must expose the audio methods, the React context
    // must have initialised api/isAvailable, AND the app must be running inside the
    // extension sidebar. When the extension is merely installed (not open), the regular
    // webapp tab has full getUserMedia access and should use the browser path. Routing
    // through the extension's offscreen document in this case fails ("Page failed to load.")
    // and also disables pause (extension API has no pause/resume). The sidebar path uses
    // sendToParent instead of the offscreen document, so isSidebarWindow correctly gates
    // which audio transport is appropriate.
    const isExtensionActive = isExtension && isExtensionAvailableInProvider && isSidebarWindow;

    // True when pause/resume is available for the current recording source.
    // Both transports now support pause/resume natively, so this is simply
    // "a recording is active via a known transport." Deriving from the transport
    // ref rather than window.chromeExtension.supportsPauseResume avoids a race
    // where the old extension build's page-api.js (no supportsPauseResume field)
    // causes checkApiAvailability to default the flag to false before
    // EXTENSION_METADATA arrives and corrects it.
    const pauseResumeSupported =
        typeof window === "undefined" ||
        activeTransportRef.current === "browser" ||
        activeTransportRef.current === "extension";

    // Listen for AUDIO_CHUNK_AVAILABLE messages posted by the sidepanel bridge
    // (sidepanel → iframe via postMessage). Accumulate chunkData strings so the
    // final blob can be assembled locally when stopAudioCapture() resolves.
    // Also restarts the silence watchdog on each chunk arrival so the "No audio detected"
    // warning fires only when the stream goes truly silent (no chunks for 3 s).
    useEffect(() => {
        const handleMessage = (event: MessageEvent) => {
            const msg = event.data;
            if (msg?.type === "AUDIO_CHUNK_AVAILABLE" && typeof msg.chunkData === "string") {
                // Accept chunks from either transport: sidepanel parent (iframe context)
                // or the content script posting into the same frame (top-level tab). See
                // audioChunkMessageGuard.ts for the full trust model.
                if (!isTrustedAudioChunkMessage(event, { win: window, extensionId: extensionIdRef.current })) return;
                const outcome = chunkAccumulatorRef.current.pushChunk(
                    msg.chunkData,
                    typeof msg.sessionId === "string" ? msg.sessionId : undefined,
                );
                if (outcome !== "accepted") {
                    // EN-719: a chunk arrived while we were between sessions. Tracked as a
                    // counter on the accumulator; surface periodic breadcrumbs so we can see
                    // the race fire in Sentry without flooding. Encode the outcome in the
                    // message string so Sentry filters can split disarmed vs wrong-session
                    // without unpacking the data payload.
                    const { droppedDisarmed, droppedWrongSession } = chunkAccumulatorRef.current.state();
                    const dropTotal = droppedDisarmed + droppedWrongSession;
                    if (dropTotal === 1 || dropTotal % 10 === 0) {
                        Sentry.addBreadcrumb({
                            category: "recording",
                            message: `chunk.${outcome}`,
                            level: "warning",
                            data: { outcome, droppedDisarmed, droppedWrongSession, chunkLen: msg.chunkData.length },
                        });
                    }
                    return;
                }
                // Chunk arrived — audio is flowing. Clear any previously-stuck silence warning
                // (the extension watchdog only sets it; it never cleared it before). Leave
                // error-class messages alone so real failures remain visible.
                setRecordingMessage((prev) => (prev?.message === "No audio detected." ? null : prev));
                // Restart the silence watchdog only while recording.
                if (!extensionSilenceWatchdogArmedRef.current) {
                    return;
                }
                restartExtensionSilenceWatchdog();
            }
        };
        window.addEventListener("message", handleMessage);
        return () => window.removeEventListener("message", handleMessage);
    }, []);

    /**
     * Calling this method results in a recording in progress being stopped and the resulting audio being present in `recordingBlob`. Sets `isRecording` to false
     */
    const stopRecording = async (options?: { discard?: boolean }) => {
        const discard = options?.discard === true;
        if (isStoppingRef.current) {
            Sentry.addBreadcrumb({
                category: "recording",
                message: "recording.stop_ignored_already_stopping",
                level: "info",
            });
            return;
        }
        isStoppingRef.current = true;
        // A cancelled start never uploads on either transport: its lease ending is not a lost recording.
        const discardedVisitId = discard ? patientRecordRef.current?.id : undefined;
        if (discardedVisitId) markRecordingDiscarded(discardedVisitId);
        // The section this stop finalizes, for the cross-tab hand-off signal (EN-1609).
        const stoppingSectionId = transcriptSectionIdRef.current;
        // Invalidates any browser start still waiting on getUserMedia (stale hourly rollover).
        browserStartGenerationRef.current++;
        // Wrap the entire stop sequence in try/finally so the concurrency guard is
        // always cleared — even if a synchronous step in the cleanup tail (setState,
        // dispatch reducer, ref clear) throws. Without this, an unhandled throw past
        // the inner try/catch would strand isStoppingRef=true and every subsequent
        // stopRecording call would no-op until page reload.
        try {
            Sentry.addBreadcrumb({
                category: "recording",
                message: "recording.session_stop",
                level: "info",
                data: {
                    transport: activeTransportRef.current,
                    transcriptSectionId: transcriptSectionIdRef.current,
                    recordingTime,
                    accumulatorChunkCount: chunkAccumulatorRef.current.state().chunkCount,
                    accumulatorDroppedDisarmed: chunkAccumulatorRef.current.state().droppedDisarmed,
                    accumulatorDroppedWrongSession: chunkAccumulatorRef.current.state().droppedWrongSession,
                },
            });
            // Disarm watchdogs before any await so late AUDIO_CHUNK_AVAILABLE cannot reschedule
            // the silence warning. The chunk accumulator is intentionally NOT disarmed here —
            // we want late tail chunks emitted during `await stopAudioCapture` to be accepted,
            // so the trailing audio isn't truncated. The accumulator is disarmed atomically
            // with snapshotAndClear() below (EN-719).
            extensionSilenceWatchdogArmedRef.current = false;
            suppressExtensionSilenceWarningRef.current = false;
            // Clear the extension silence watchdog before stopping so it cannot fire after stop.
            if (silenceWatchdogRef.current) {
                clearTimeout(silenceWatchdogRef.current);
                silenceWatchdogRef.current = null;
            }
            setRecordingMessage(null);
            _stopTimer();
            clearInterval(hourIntervalRef.current as any);
            setRecordingTime(0);
            setActiveRecordingId("");
            hourCounterRef.current = 0;

            let extensionSaveError: unknown = null;
            if (activeTransportRef.current === "extension") {
                try {
                    // Extension-transport: chunks stream in via AUDIO_CHUNK_AVAILABLE postMessages
                    // (accumulator). Request includeData from the extension content-script path as
                    // a safety net — blobFromStreamedChunksOrStopResult prefers streamed chunks.
                    const stopResult = await stopAudioCapture(!isSidebarWindow);

                    // Snapshot first (captures any tail chunks that arrived during the await),
                    // then disarm so chunks emitted during closeAudioCapture can't sneak into
                    // the next recording's accumulator (EN-719).
                    const streamedChunks = chunkAccumulatorRef.current.snapshotAndClear();
                    chunkAccumulatorRef.current.disarm();
                    const blob = blobFromStreamedChunksOrStopResult(streamedChunks, stopResult, mimeType);

                    // A cancelled start (discard) stops and closes the capture but never uploads.
                    if (
                        !discard &&
                        blob &&
                        onAudioDataAvailableRef.current &&
                        patientRecordRef.current &&
                        transcriptSectionIdRef.current
                    ) {
                        const audioFile = await recoverIfPrefixed(createAudioFile(blob), "extension");
                        await onAudioDataAvailableRef.current(
                            audioFile,
                            patientRecordRef.current,
                            transcriptSectionIdRef.current,
                            0,
                            true,
                        );
                    }

                    await closeAudioCapture();
                } catch (error) {
                    // stopAudioCapture, snapshot, upload, or closeAudioCapture threw — the
                    // snapshot+disarm in the success path never ran, so the accumulator is still
                    // armed with the stale sessionId. Disarm now so late chunks don't keep
                    // accumulating into a dead session until the next arm() (EN-719).
                    chunkAccumulatorRef.current.disarm();
                    extensionSaveError = error;
                    Sentry.captureException(error, {
                        tags: { errorCategory: "recording", errorId: "recording.extension_stop_failed" },
                        extra: buildRecordingErrorExtra({
                            recordingState,
                            recordingTime,
                            activeRecordingId,
                            transcriptSectionId: transcriptSectionIdRef.current,
                            mediaRecorderState: mediaRecorder?.state,
                        }),
                    });
                    setRecordingMessage({ message: "Recording failed to save — please try again.", type: "error" });
                    try {
                        await closeAudioCapture();
                    } catch {
                        // ignore — best-effort release of extension capture
                    }
                }
                // The extension path hands its final audio off inline above.
                settleHandoff(stoppingSectionId, extensionSaveError === null);
            } else {
                // reset recorder
                Sentry.addBreadcrumb({
                    category: "recording",
                    message: "recording.media_recorder_stop",
                    level: "info",
                    data: {
                        reason: "user_stop",
                        recordingTime,
                        activeRecordingId,
                        transcriptSectionId: transcriptSectionIdRef.current,
                        mediaRecorderState: mediaRecorder?.state,
                    },
                });
                // EN-837: bridge the stop→upload handoff (browser transport). mediaRecorder.stop()
                // emits the terminal `dataavailable` on a LATER macrotask, and the just-recorded
                // clip's `isUploadingGlobal` only flips true then (terminal handler → uploadAudio).
                // Without this, the synchronous `inactive` dispatch below is a false-idle tick a
                // deferred teardown (version reload, sign-out) would fire on — tearing the page down
                // before the final clip uploads. `finalizing` keeps the recorder "busy" across the
                // gap; uploadAudio clears it once `isUploadingGlobal` takes over, and the watchdog
                // bounds it (releases after maxDeferralMs) if the terminal event never uploads. Guard
                // on an active recorder so a no-op stop() (already inactive/undefined) can't set a
                // flag nothing will clear.
                //
                // EN-837 finding #2: key the bridge by this recording's section id so overlapping
                // recordings can't clobber each other's `finalizing` (a second recording stopping while
                // this one's terminal `ondataavailable` is still in flight would otherwise clear the
                // second's bridge). `transcriptSectionIdRef.current` here is the active recorder's id and
                // equals the closure id the terminal handler / uploadAudio clear with — capture it before
                // the ref is nulled at the end of this function.
                const live = liveBrowserRecorderRef.current;
                const recorder = live?.recorder ?? mediaRecorder;
                if (discard && live) {
                    // A cancelled start: drop the terminal chunk instead of saving and uploading it.
                    // Nothing then settles a finalizing bridge, so none is armed, and the handler
                    // that closes the AudioContext at stop will not run, so close it here.
                    live.recorder.ondataavailable = null;
                    if (live.audioContext.state !== "closed") void live.audioContext.close();
                }
                const finalizingKey = transcriptSectionIdRef.current;
                if (!discard && finalizingKey && recorder && recorder.state !== "inactive") {
                    dispatch(finalizingStarted(finalizingKey));
                }
                recorder?.stop();
                recorder?.stream.getTracks().forEach((t) => t.stop());
            }

            // Clear refs at the end after they've been used — always runs regardless of extension error.
            patientRecordRef.current = null;
            setRecordingVisit(null);
            transcriptSectionIdRef.current = null;
            onAudioDataAvailableRef.current = null;

            setRecordingState(RecordingState.inactive);
            dispatch(setGlobalRecordingState(RecordingState.inactive));
            setMediaRecorder(undefined);
            liveBrowserRecorderRef.current = null;
            setAudioStream(undefined);
            activeTransportRef.current = null;

            // Rethrow after cleanup so callers can reset widget state instead of showing false success.
            // The finally below clears isStoppingRef whether we throw here or fall through normally,
            // so a caller that catches and retries will pass the concurrency guard cleanly.
            if (extensionSaveError) throw extensionSaveError;
        } finally {
            isStoppingRef.current = false;
        }
    };
    // The latest stopRecording (it closes over render state) for the cross-tab stop-and-save responder.
    stopRecordingRef.current = () => stopRecording();

    const stopErroredRecording = async (error: string) => {
        // Always surface the error to the user before any concurrency check — the
        // user's stop is the load-bearing path; this is a status update.
        setRecordingMessage({ message: error, type: "error" });
        // Triggered by MediaStreamTrack onmute/onended — external browser events that
        // can fire mid-stopRecording (e.g. headset unplug during the stopAudioCapture
        // await). Without this guard both paths race on accumulator disarm and ref
        // nulling, producing the same stale-session corruption EN-719 just fixed.
        if (isStoppingRef.current) {
            Sentry.addBreadcrumb({
                category: "recording",
                message: "recording.error_stop_ignored_already_stopping",
                level: "info",
                data: { error },
            });
            return;
        }
        isStoppingRef.current = true;
        // Invalidates any browser start still waiting on getUserMedia (stale hourly rollover).
        browserStartGenerationRef.current++;
        try {
            _handleIncomplete(patientRecordRef.current as PatientRecord);

            if (activeTransportRef.current === "extension") {
                extensionSilenceWatchdogArmedRef.current = false;
                suppressExtensionSilenceWarningRef.current = false;
                chunkAccumulatorRef.current.disarm();
                if (silenceWatchdogRef.current) {
                    clearTimeout(silenceWatchdogRef.current);
                    silenceWatchdogRef.current = null;
                }
                try {
                    await stopAudioCapture(false);
                    await closeAudioCapture();
                } catch {
                    // ignore
                }
            }

            _stopTimer();
            clearInterval(hourIntervalRef.current as any);

            setRecordingTime(0);
            setActiveRecordingId("");
            patientRecordRef.current = null;
            setRecordingVisit(null);
            transcriptSectionIdRef.current = null;
            onAudioDataAvailableRef.current = null;
            activeTransportRef.current = null;
        } finally {
            isStoppingRef.current = false;
        }
    };

    /**
     * Calling this method results in a recording in progress being stopped and the resulting audio being present in `recordingBlob`. Sets `isRecording` to false
     * @description Resets the recorder
     * @returns `void`
     * @description Resets the recorder
     */
    const resetRecorder = async (force: boolean = false) => {
        // Do not clear an active error message unless the caller explicitly forces the reset.
        // The orchestrator (AssetLibraryProvider) calls resetRecorder(true) only after the
        // user has had a chance to see the error (e.g. after a setTimeout delay).
        if (!force && recordingMessage?.type === "error") {
            return;
        }
        setRecordingState(RecordingState.loading);

        // Use the transport that was active when recording started so the reset path matches
        // the path that started the recording. Falls back to isExtensionActive when called
        // before any recording has begun (e.g. initial mic probe on first load).
        // resetRecorder runs in the inactive state, but a prior aborted start could have
        // left the accumulator armed. Disarm before the reset's mic probe so the probe's
        // startAudioCapture / stopAudioCapture cycle can't push leftover chunks into a
        // dead session (EN-719).
        chunkAccumulatorRef.current.disarm();

        const useExtensionPath =
            activeTransportRef.current === "extension" || (activeTransportRef.current === null && isExtensionActive);
        if (useExtensionPath) {
            try {
                // No owner fields: this probe skips the cross-tab stop-and-save gate, so a paused
                // capture it finds may belong to a live tab. Without them the extension refuses to
                // replace it, as before EN-1609.
                const result = await startExtensionCapture({
                    mimeType: mimeType,
                });

                if (result?.error) {
                    throw new Error(result.error);
                }

                await stopAudioCapture(false);
                await closeAudioCapture();

                setRecordingState(RecordingState.inactive);
                dispatch(setGlobalRecordingState(RecordingState.inactive));
                patientRecordRef.current = null;
                setRecordingVisit(null);
                transcriptSectionIdRef.current = null;
                onAudioDataAvailableRef.current = null;
                setRecordingMessage(null);
                suppressExtensionSilenceWarningRef.current = false;
            } catch (err: any) {
                const isPermissionError =
                    err?.message?.toLowerCase().includes("permission") ||
                    err?.message?.toLowerCase().includes("denied");
                setRecordingMessage({
                    message: isPermissionError
                        ? "Microphone access denied. Please grant permission in the HappyDoc extension options."
                        : "Error resetting microphone via extension.",
                    type: "error",
                });
                Sentry.captureException(err, {
                    tags: { errorCategory: "recording", errorId: "recording.reset_extension_failed" },
                    extra: buildRecordingErrorExtra({
                        recordingState,
                        recordingTime,
                        activeRecordingId,
                        transcriptSectionId: transcriptSectionIdRef.current,
                        mediaRecorderState: mediaRecorder?.state,
                    }),
                });
                setRecordingState(RecordingState.error);
                dispatch(setGlobalRecordingState(RecordingState.error));
            }
            activeTransportRef.current = null;
            return;
        }

        navigator.mediaDevices
            .getUserMedia({ audio: true })
            .then(async (stream) => {
                const recorder = new MediaRecorder(stream, { mimeType: mimeType });

                if (!recorder) {
                    setRecordingState(RecordingState.error);
                    dispatch(setGlobalRecordingState(RecordingState.error));
                    activeTransportRef.current = null;
                    return;
                }

                recorder.stop();

                recorder.ondataavailable = (_event: BlobEvent) => {
                    recorder.stream.getTracks().forEach((t) => t.stop());
                    setRecordingState(RecordingState.inactive);
                    dispatch(setGlobalRecordingState(RecordingState.inactive));
                };
                recorder.stream.getTracks().forEach((t) => t.stop());

                setRecordingState(RecordingState.inactive);
                dispatch(setGlobalRecordingState(RecordingState.inactive));
                patientRecordRef.current = null;
                setRecordingVisit(null);
                transcriptSectionIdRef.current = null;
                onAudioDataAvailableRef.current = null;
                setRecordingMessage(null);
                activeTransportRef.current = null;
            })
            .catch((err: DOMException) => {
                // handle error
                setRecordingMessage(
                    err?.message === "Permission denied"
                        ? { message: "Please allow microphone access.", type: "error" }
                        : err?.message === "Requested device not found"
                          ? { message: "Microphone not found. Check settings.", type: "error" }
                          : { message: "Error setting up microphone.", type: "error" },
                );
                Sentry.captureException(err, {
                    tags: { errorCategory: "recording", errorId: "recording.reset_browser_failed" },
                    extra: buildRecordingErrorExtra({
                        recordingState,
                        recordingTime,
                        activeRecordingId,
                        transcriptSectionId: transcriptSectionIdRef.current,
                        mediaRecorderState: mediaRecorder?.state,
                    }),
                });
                setRecordingState(RecordingState.error);
                dispatch(setGlobalRecordingState(RecordingState.error));
                activeTransportRef.current = null;
            });
    };

    // every hour stop and restart the recorder
    // use the recording timer as the clock
    const RESTART_INTERVAL = 60 * 60; // 1 hour seconds
    useEffect(() => {
        // if recordingTime is on the hour mark, stop and restart the recorder
        if (recordingTime > 0 && recordingTime % RESTART_INTERVAL === 0) {
            // limit to 3 hours
            if (hourCounterRef.current >= 2) {
                setRecordingMessage({ message: "Recording stopped after 3 hours.", type: "error" });

                // stop recording
                if (recordingState === RecordingState.recording) {
                    stopRecording();
                }

                hourCounterRef.current = 0;
                return;
            }

            if (patientRecordRef.current && transcriptSectionIdRef.current && onAudioDataAvailableRef.current) {
                if (activeTransportRef.current === "extension") {
                    // Extension-mode: stop the current capture session, flush accumulated chunks
                    // to the upload pipeline, clear the accumulator, then start a new session.
                    // Steps must happen in this order to avoid data loss.
                    const uploadPatientRecord = patientRecordRef.current;
                    const uploadTranscriptSectionId = transcriptSectionIdRef.current;
                    const uploadOnAudioDataAvailable = onAudioDataAvailableRef.current;
                    transcriptSectionIdRef.current = uuidv4();
                    // Arm telemetry for the new rollover session. The browser-rollover branch
                    // re-enters startRecording (which calls captureSessionEnvironment); the
                    // extension branch stops + starts capture without re-entering startRecording,
                    // so without this call client_metadata would be null on every hour-2+ upload.
                    captureSessionEnvironment(transcriptSectionIdRef.current, {
                        transport: "extension",
                        mimeType: mimeType ?? null,
                        extensionVersion: extensionVersion ?? null,
                        extensionId: extensionId ?? null,
                        isSidebarWindow,
                        pauseResumeSupported: !!window.chromeExtension?.supportsPauseResume,
                    });
                    setRecordingMessage({ message: "Last hour of recording saving...", type: "info" });
                    (async () => {
                        try {
                            // Clear silence watchdog before the stop; leave the accumulator armed so
                            // tail chunks during stopAudioCapture aren't truncated. Snapshot + disarm
                            // happen atomically right after (EN-719).
                            extensionSilenceWatchdogArmedRef.current = false;
                            if (silenceWatchdogRef.current) {
                                clearTimeout(silenceWatchdogRef.current);
                                silenceWatchdogRef.current = null;
                            }
                            const stopResult = await stopAudioCapture(!isSidebarWindow);
                            const streamedChunks = chunkAccumulatorRef.current.snapshotAndClear();
                            chunkAccumulatorRef.current.disarm();
                            const blob = blobFromStreamedChunksOrStopResult(streamedChunks, stopResult, mimeType);
                            if (blob && blob.size > 0) {
                                // Pass uploadTranscriptSectionId / uploadPatientRecord.id explicitly:
                                // the refs may have moved on or been cleared by the time the awaits in
                                // recoverIfPrefixed resolve. Captured-by-value locals are the only safe
                                // source for Sentry forensic correlation in this path (EN-719).
                                const audioFile = await recoverIfPrefixed(
                                    createAudioFile(blob),
                                    "extension",
                                    uploadTranscriptSectionId,
                                    uploadPatientRecord.id,
                                );
                                if (containsFeatureFlag(FeatureFlags.CHUNK_UPLOAD)) {
                                    chunkBuffer.current.push({
                                        transcriptSectionId: uploadTranscriptSectionId,
                                        transcriptionId: uploadPatientRecord.transcriptions?.id || "",
                                        patientRecordId: uploadPatientRecord.id || "",
                                        chunkIndex: 0,
                                        success: false,
                                        uploading: false,
                                        data: audioFile,
                                        retryCount: 0,
                                    });
                                }
                                await uploadOnAudioDataAvailable(
                                    audioFile,
                                    uploadPatientRecord,
                                    uploadTranscriptSectionId,
                                    0,
                                    true,
                                );
                            }
                            // Re-arm with the NEW transcript section id (reassigned to a fresh uuid
                            // at the top of the rollover; see the `transcriptSectionIdRef.current =
                            // uuidv4()` immediately before this IIFE). Fall back to the previous id
                            // only if the new one is missing — arm() throws on empty string so a bad
                            // id won't silently lose the next hour's audio.
                            const nextSectionId = transcriptSectionIdRef.current || uploadTranscriptSectionId;
                            chunkAccumulatorRef.current.arm(nextSectionId);
                            try {
                                const startResult = await startExtensionCapture({
                                    mimeType,
                                    visitId: uploadPatientRecord?.id ?? null,
                                    transcriptSectionId: nextSectionId,
                                });
                                if (startResult?.error) {
                                    throw new Error(startResult.error);
                                }
                            } catch (startErr) {
                                // startAudioCapture failed AFTER we armed. Disarm so late chunks from
                                // the half-initialized session don't accumulate into a dead recording.
                                chunkAccumulatorRef.current.disarm();
                                throw startErr;
                            }
                            // Restart silence watchdog for the new hour's capture session.
                            extensionSilenceWatchdogArmedRef.current = true;
                            restartExtensionSilenceWatchdog();
                            setRecordingMessage(null);
                            suppressExtensionSilenceWarningRef.current = false;
                        } catch (err) {
                            // Same hazard as the stopRecording catch: if stopAudioCapture, the
                            // upload, or the success-path arm threw before snapshot+disarm ran,
                            // the accumulator is still armed with the old session id. Disarm so
                            // orphan chunks can't accumulate before the retry below re-arms with
                            // the new id (EN-719).
                            chunkAccumulatorRef.current.disarm();
                            // Best-effort release: if stopAudioCapture itself threw, the offscreen
                            // mic stream is still live and the retry startAudioCapture below would
                            // hit "capture already in progress". Mirrors stopRecording's catch.
                            try {
                                await closeAudioCapture();
                            } catch {
                                // ignore — best-effort release of extension capture
                            }
                            Sentry.captureException(err, {
                                tags: { errorCategory: "recording", errorId: "recording.rollover_stop_failed" },
                                // Use uploadTranscriptSectionId — the ref was reassigned to the new session's
                                // uuid above (line 787), but this failure belongs to the old session.
                                extra: buildRecordingErrorExtra({
                                    recordingState,
                                    recordingTime,
                                    activeRecordingId,
                                    transcriptSectionId: uploadTranscriptSectionId,
                                    mediaRecorderState: mediaRecorder?.state,
                                }),
                            });
                            suppressExtensionSilenceWarningRef.current = true;
                            setRecordingMessage({
                                message: "Last hour segment failed to save — recording may be incomplete.",
                                type: "error",
                            });
                            try {
                                // Re-arm before starting so we don't drop the new hour's leading chunk.
                                if (transcriptSectionIdRef.current) {
                                    chunkAccumulatorRef.current.arm(transcriptSectionIdRef.current);
                                }
                                // If we couldn't arm (no section id), leave the accumulator disarmed —
                                // the next hour will fail to capture chunks but we won't lose them
                                // silently into a stale session. The user will see the silence warning.
                                const startResult = await startExtensionCapture({
                                    mimeType,
                                    visitId: patientRecordRef.current?.id ?? null,
                                    transcriptSectionId: transcriptSectionIdRef.current,
                                });
                                if (startResult?.error) {
                                    throw new Error(startResult.error);
                                }
                                // Restart silence watchdog even after the error-recovery start.
                                extensionSilenceWatchdogArmedRef.current = true;
                                restartExtensionSilenceWatchdog();
                            } catch (resumeErr) {
                                // Recovery start failed — disarm so late chunks don't accumulate into
                                // a dead session.
                                chunkAccumulatorRef.current.disarm();
                                // Best-effort release of any lingering offscreen capture so the mic
                                // stream isn't left open until the user manually stops.
                                try {
                                    await closeAudioCapture();
                                } catch {
                                    // ignore — best-effort release of extension capture
                                }
                                // Recovery start failed — the next hour will record dead air silently.
                                // Surface a terminal error so the user stops recording instead of
                                // believing the previous "segment failed but recording continues"
                                // message at line 828 (which is now stale).
                                setRecordingMessage({
                                    message:
                                        "Recording could not resume after error. Please stop and start a new clip.",
                                    type: "error",
                                });
                                setRecordingState(RecordingState.error);
                                dispatch(setGlobalRecordingState(RecordingState.error));
                                Sentry.captureException(resumeErr, {
                                    tags: { errorCategory: "recording", errorId: "recording.rollover_resume_failed" },
                                    // Use uploadTranscriptSectionId — same reasoning as rollover_stop_failed above.
                                    extra: buildRecordingErrorExtra({
                                        recordingState,
                                        recordingTime,
                                        activeRecordingId,
                                        transcriptSectionId: uploadTranscriptSectionId,
                                        mediaRecorderState: mediaRecorder?.state,
                                    }),
                                });
                            }
                        }
                    })();
                } else {
                    // create new transcript section id because we have split the clip into new recordings
                    transcriptSectionIdRef.current = uuidv4();

                    mediaRecorder?.stop();
                    mediaRecorder?.stream.getTracks().forEach((t) => t.stop());

                    setRecordingMessage({ message: "Last hour of recording saving...", type: "info" });

                    setMediaRecorder(undefined);
                    liveBrowserRecorderRef.current = null;
                    setAudioStream(undefined);
                    _stopTimer();

                    // The browser start rejects with MicStartError when the mic will not reopen. The last
                    // hour is already stopped and uploads from its own recorder's handler, so show the
                    // legacy message and enter no_permission without touching refs. A setup failure after
                    // the mic opens is handled inside startRecording. Any other error (before the mic
                    // opens) is reported and shown the same way: rethrowing in this fire-and-forget
                    // timer would only become an unhandled rejection the vet never sees.
                    startRecording(
                        patientRecordRef.current as PatientRecord,
                        transcriptSectionIdRef.current,
                        onAudioDataAvailableRef.current,
                        { reuseSessionAudio: true },
                    ).catch((err: unknown) => {
                        if (err instanceof MicStartError) {
                            setRecordingMessage({ message: err.message, type: "error" });
                        } else {
                            Sentry.captureException(err, {
                                tags: { errorCategory: "recording", errorId: "recording.rollover_restart_failed" },
                            });
                            setRecordingMessage({ message: "Error setting up microphone.", type: "error" });
                        }
                        setRecordingState(RecordingState.no_permission);
                        dispatch(setGlobalRecordingState(RecordingState.no_permission));
                    });
                }
            }

            hourCounterRef.current++;
        }
    }, [recordingTime]);

    /**
     * Calling this method would result in the recording to start. Sets `isRecording` to true
     */
    async function startRecording(
        patientRecord: PatientRecord,
        transcriptSectionId: string,
        onAudioDataAvailable: (
            audioFile: File,
            patientRecord: PatientRecord,
            transcriptSectionId: string,
            chunkIndex: number,
            end: boolean,
        ) => Promise<void>,
        options?: { reuseSessionAudio?: boolean },
    ) {
        if (timerIntervalRef.current != null) {
            Sentry.addBreadcrumb({
                message: "Timer interval already exists",
                level: "warning",
                data: { recordingState, recordingTime, activeRecordingId, mimeType },
            });
        }

        if (!patientRecord || !patientRecord?.transcriptions?.id || !patientRecord?.id) {
            Sentry.captureException(new Error("Patient record data is missing when starting recording"), {
                tags: { errorCategory: "recording", errorId: "recording.start_missing_patient_record" },
                extra: {
                    recordingState,
                    activeRecordingId,
                    mimeType,
                    patientRecordIsNotDefined: patientRecord === null || patientRecord === undefined,
                    patientRecordId: patientRecord?.id,
                    patientRecordTranscriptionsId: patientRecord?.transcriptions?.id,
                },
            });

            throw new Error("Error starting recording, failed to fetch patient record");
        }

        setRecordingState(RecordingState.loading);

        // A fresh start begins a new 3-hour budget. A rollover whose reopen failed never reaches
        // stopRecording, so its stale hour count and clock would cut this recording short.
        if (!options?.reuseSessionAudio) {
            hourCounterRef.current = 0;
            setRecordingTime(0);
            // A failed extension rollover leaves its clock running; a second interval would double the clock.
            _stopTimer();
        }

        // Check available storage and clear up space if needed
        localAudioDb.checkStorage();

        if (isExtensionActive) {
            activeTransportRef.current = "extension";
            captureSessionEnvironment(transcriptSectionId, {
                transport: "extension",
                mimeType: mimeType ?? null,
                extensionVersion: extensionVersion ?? null,
                extensionId: extensionId ?? null,
                isSidebarWindow,
                // Compute inline from extension capability, not from the render-time
                // `pauseResumeSupported` closure: that value was computed BEFORE
                // activeTransportRef was set to "extension" this turn, so it can be stale.
                pauseResumeSupported: !!window.chromeExtension?.supportsPauseResume,
            });
            Sentry.addBreadcrumb({
                category: "recording",
                message: "recording.session_start",
                level: "info",
                data: {
                    transport: "extension",
                    transcriptSectionId,
                    extensionInstalled: isExtensionAvailableInProvider,
                    // Accumulator counters are extension-only — the browser-transport path doesn't
                    // touch the accumulator (it gets its chunks via MediaRecorder.ondataavailable,
                    // not postMessage), so these fields only appear on this breadcrumb. The
                    // browser-path session_start breadcrumb below intentionally omits them.
                    // Counters reflect the just-completed session (accumulator resets them on arm).
                    prevSessionDroppedDisarmed: chunkAccumulatorRef.current.state().droppedDisarmed,
                    prevSessionDroppedWrongSession: chunkAccumulatorRef.current.state().droppedWrongSession,
                },
            });
            try {
                // Arm the chunk accumulator BEFORE awaiting startAudioCapture. The offscreen
                // doc can emit its first AUDIO_CHUNK_AVAILABLE (carrying the EBML header) before
                // the START response reaches this iframe; arming after the await would drop that
                // chunk. Arming with the new session id also clears any leftover state and
                // ensures the previous session's late chunks (which arrive while disarmed) are
                // counted as dropped_disarmed rather than silently accepted (EN-719).
                chunkBuffer.current = [];
                blobIndexRef.current = 0;
                chunkAccumulatorRef.current.arm(transcriptSectionId);

                const result = await startExtensionCapture({
                    mimeType: mimeType,
                    // EN-1609: who the session is for, handed back if a later start replaces it while paused.
                    visitId: patientRecord.id ?? null,
                    transcriptSectionId,
                });

                if (result?.error) {
                    throw new Error(result.error);
                }

                // A start owns the session refs outright (same as the browser path): after a failed
                // rollover resume they still hold the dead session's visit and callback.
                patientRecordRef.current = patientRecord;
                // EN-1088: best-effort recording lease on this visit, so merge and bulk archive leave it alone.
                setRecordingVisit(patientRecord.id ?? null);
                transcriptSectionIdRef.current = transcriptSectionId;
                onAudioDataAvailableRef.current = onAudioDataAvailable;

                setRecordingState(RecordingState.recording);
                dispatch(setGlobalRecordingState(RecordingState.recording));
                // The failed session's timer is still running; a second interval would double the clock.
                _stopTimer();
                _startTimer();
                setRecordingMessage(null);
                suppressExtensionSilenceWarningRef.current = false;

                // Start silence watchdog: warn if no AUDIO_CHUNK_AVAILABLE arrives within 3 s.
                extensionSilenceWatchdogArmedRef.current = true;
                restartExtensionSilenceWatchdog();

                if (activeRecordingId === "") setActiveRecordingId(patientRecord.id as string);
                return;
            } catch (err: any) {
                activeTransportRef.current = null;
                chunkAccumulatorRef.current.disarm();
                const isPermissionError =
                    err?.message?.toLowerCase().includes("permission") ||
                    err?.message?.toLowerCase().includes("denied");
                setRecordingMessage({
                    message: isPermissionError
                        ? "Microphone access denied. Please grant permission in the HappyDoc extension options."
                        : "Error starting recording via extension.",
                    type: "error",
                });
                Sentry.captureException(err, {
                    tags: { errorCategory: "recording", errorId: "recording.start_extension_failed" },
                    extra: buildRecordingErrorExtra({
                        recordingState,
                        recordingTime,
                        activeRecordingId,
                        transcriptSectionId,
                    }),
                });
                setRecordingState(RecordingState.error);
                dispatch(setGlobalRecordingState(RecordingState.error));
                return;
            }
        }

        // A fresh start opens a new session generation; the hourly rollover continues the current one.
        if (!options?.reuseSessionAudio) browserStartGenerationRef.current++;
        const startGeneration = browserStartGenerationRef.current;
        // Stale-start race: an hourly rollover waits on getUserMedia (seconds on a Bluetooth reconnect).
        // If the vet stops (and maybe starts visit B) meanwhile, this start must not reclaim the refs,
        // the lease, or start a phantom recorder that files B's audio under the old visit.
        const isStaleStart = () => startGeneration !== browserStartGenerationRef.current;
        const addStaleStartBreadcrumb = () =>
            Sentry.addBreadcrumb({
                category: "recording",
                message: "recording.stale_start_discarded",
                level: "info",
                data: { reuseSessionAudio: options?.reuseSessionAudio === true, transcriptSectionId },
            });

        captureSessionEnvironment(transcriptSectionId, {
            transport: "browser",
            mimeType: mimeType ?? null,
            // Browser-transport recordings don't go through the extension even when it's
            // installed, but we capture extension presence + version anyway so post-hoc
            // SQL can answer "was the extension also present at recording time?".
            extensionVersion: extensionVersion ?? null,
            extensionId: extensionId ?? null,
            isSidebarWindow,
            // Browser MediaRecorder natively supports pause/resume. Use a literal here
            // rather than the render-time `pauseResumeSupported` closure, which can
            // resolve to `false` when the extension is installed but its provider
            // hasn't initialised (extension capability checks dominate the formula).
            pauseResumeSupported: true,
        });
        Sentry.addBreadcrumb({
            category: "recording",
            message: "recording.session_start",
            level: "info",
            data: { transport: "browser", transcriptSectionId, extensionInstalled: isExtensionAvailableInProvider },
        });

        // EN-1499: prefer the remembered mic (ideal, so a missing one falls back to the default).
        // The hourly rollover reuses what the session's first start resolved.
        const reused = options?.reuseSessionAudio ? sessionAudioConstraintsRef.current : null;
        const audio = reused ?? (await resolveStartAudioConstraints(audioTrackConstraints));
        sessionAudioConstraintsRef.current = audio;
        // Set once the mic opens, so a later setup failure can release it.
        let openedStream: MediaStream | undefined;
        let openedContext: AudioContext | undefined;

        // Returned so the caller's await settles once the mic is open and the recorder is set up (or the start failed).
        return openMicStream(audio, audioTrackConstraints ?? true)
            .catch((err: unknown): null => {
                // Stale-start race (see isStaleStart): a superseded start owns nothing, so its failure
                // must not reset state, clear refs, or surface an error over the session that replaced it.
                if (isStaleStart()) {
                    addStaleStartBreadcrumb();
                    return null;
                }
                // EN-1499: the mic did not open. Nothing started, so return to idle and let the caller
                // classify the failure (by err.name) and show it; no message is set here.
                const rawName = (err as { name?: unknown } | null)?.name;
                const name = typeof rawName === "string" ? rawName : "unknown";
                Sentry.addBreadcrumb({
                    category: "recording",
                    message: "recording.mic_start_failed",
                    level: "warning",
                    data: { name, transcriptSectionId },
                });
                // An hourly rollover keeps its refs and visit lease: the last hour is still finalizing
                // and the vet's Stop reads them. The rollover caller sets the error state.
                if (!options?.reuseSessionAudio) {
                    setRecordingState(RecordingState.inactive);
                    dispatch(setGlobalRecordingState(RecordingState.inactive));
                    setRecordingVisit(null);
                    activeTransportRef.current = null;
                    patientRecordRef.current = null;
                    transcriptSectionIdRef.current = null;
                    onAudioDataAvailableRef.current = null;
                }
                throw new MicStartError(legacyMicStartMessage(name), err);
            })
            .then(async (stream) => {
                if (stream === null) return; // stale start that failed; handled in .catch above
                // Stale-start race: Stop (and maybe a fresh start for another visit) happened while this
                // start waited on getUserMedia. Release the mic and leave every ref and state alone.
                // Everything from this check to recorder.start() is synchronous on purpose
                // (captureAudioInputDevices is fire-and-forget), so the start generation cannot change in
                // between. Adding an `await` in that span would reopen the stale-start race.
                if (isStaleStart()) {
                    stream.getTracks().forEach((t) => t.stop());
                    addStaleStartBreadcrumb();
                    return;
                }
                openedStream = stream;
                activeTransportRef.current = "browser";
                captureActiveMicAndSettings(transcriptSectionId, stream);
                // Enumerate full device set AFTER permission is granted so labels are
                // populated. Fire-and-forget: the device list isn't read until upload
                // (useFileUpload → getClientMetadata), so blocking start by up to
                // ENUMERATE_DEVICES_TIMEOUT_MS would just cost first-words latency.
                void captureAudioInputDevices(transcriptSectionId);
                Sentry.addBreadcrumb({
                    category: "recording",
                    message: "recording.mic_selected",
                    level: "info",
                    data: { transcriptSectionId, micDevice: stream.getAudioTracks()[0]?.label ?? null },
                });
                // A fresh start owns the session refs outright. Concurrent starts are prevented upstream
                // (AssetLibraryProvider startInFlightRef and mobileWarningRef, plus recordingState); the old
                // set-if-null guard made a retry after a failed session file audio under the dead session's visit.
                // After a failed rollover or setup the refs still hold that dead visit and callback.
                // A rollover passes the refs' own values back in, so assigning is a no-op for it.
                patientRecordRef.current = patientRecord;
                transcriptSectionIdRef.current = transcriptSectionId;
                onAudioDataAvailableRef.current = onAudioDataAvailable;

                setAudioStream(stream);

                // Kept only to read the sample rate for telemetry; closed at stop.
                const audioContext = new AudioContext();
                openedContext = audioContext;
                setAudioContextSampleRate(transcriptSectionId, audioContext.sampleRate);

                const recorder: MediaRecorder = new MediaRecorder(stream, {
                    ...mediaRecorderOptions,
                    mimeType: mimeType,
                });

                setMediaRecorder(recorder);
                liveBrowserRecorderRef.current = { recorder, audioContext };
                setRecordingState(RecordingState.recording);

                dispatch(setGlobalRecordingState(RecordingState.recording));

                Sentry.addBreadcrumb({
                    category: "recording",
                    message: "recording.media_recorder_start",
                    level: "info",
                    data: {
                        transcriptSectionId,
                        mimeType,
                        timeslice: recorderTimeslice,
                        audioContextState: audioContext?.state,
                    },
                });
                recorder.start(recorderTimeslice);
                // EN-1088: best-effort recording lease on this visit, so merge and bulk archive leave it alone.
                // Taken once the recorder has started, so a start that fails never leaves a lease behind.
                setRecordingVisit(patientRecordRef.current?.id ?? null);

                _startTimer();

                setRecordingMessage(null);
                chunkBuffer.current = [];
                blobIndexRef.current = 0;

                // set active recording id
                if (activeRecordingId === "") setActiveRecordingId(patientRecord.id as string);

                // handle track events
                // EN-1499 (D3, D8, approved): a mute is logged, not stopped and not warned. It is often a Bluetooth
                // glitch or AirPods hand-off, and stopping lost the rest of the visit; the live level bars are the cue until slice 2 (R23 silence alert).
                const addTrackMutedBreadcrumb = (atStart: boolean) =>
                    Sentry.addBreadcrumb({
                        category: "recording",
                        message: "recording.track_muted",
                        level: "warning",
                        data: { transcriptSectionId, recordingTime: recordingTimeRef.current, atStart },
                    });
                stream.getTracks().forEach((t) => {
                    if (t.muted) addTrackMutedBreadcrumb(true);

                    t.onmute = () => addTrackMutedBreadcrumb(false);
                    t.onended = (event) => {
                        Sentry.addBreadcrumb({
                            message: "Recording ended by browser",
                            level: "warning",
                            data: { recordingState, recordingTime, activeRecordingId, mimeType, event },
                        });
                        recorder.stop();
                        hasMicrophonePermission().then((hasPermission) => {
                            if (hasPermission) {
                                setRecordingState(RecordingState.error);
                                dispatch(setGlobalRecordingState(RecordingState.error));
                                stopErroredRecording("Recording ended by browser.");
                            } else {
                                setRecordingState(RecordingState.no_permission);
                                dispatch(setGlobalRecordingState(RecordingState.no_permission));
                                stopErroredRecording("Please allow microphone access.");
                            }
                        });
                    };
                });
                // handle recorder events
                recorder.onerror = (e) => {
                    stopRecording();
                    setRecordingMessage({ message: "An error occurred. Recording stopped.", type: "error" });
                    Sentry.captureException(e, {
                        tags: { errorCategory: "recording", errorId: "recording.media_recorder_error" },
                        extra: buildRecordingErrorExtra({
                            recordingState,
                            recordingTime,
                            activeRecordingId,
                            mimeType,
                            mediaRecorderState: recorder.state,
                            audioContextState: audioContext?.state,
                            transcriptSectionId: transcriptSectionIdRef.current,
                        }),
                    });
                    if (!isRecordingError(recordingState)) {
                        setRecordingState(RecordingState.error);
                        dispatch(setGlobalRecordingState(RecordingState.error));
                    }
                };

                // Per-session persistence state, scoped to THIS recorder (closed over by the handler
                // below) rather than shared hook refs. Hourly rollover stops this recorder and
                // synchronously starts the next while this recorder's async stop handler is still
                // pending; per-session state keeps each hour's accumulated chunks, write-coalescing
                // guard, and in-flight snapshot isolated so the new session can't reset or clobber the
                // old one's buffer (which would upload the wrong hour's audio or lose it).
                //
                // sessionChunks: raw MediaRecorder timeslice chunks for a persistence-only recording
                // (CHUNK_UPLOAD off); concatenating them reproduces the original valid WebM/MP4 file.
                // isSnapshotPersisting: coalesces overlapping IndexedDB snapshot writes (each snapshot
                // re-serializes the whole file, so a new tick can arrive before the previous finishes).
                // sessionSnapshotInFlight: the current mid snapshot write; the stop path awaits it so a
                // slow mid write can't land after the final owned write and re-open the recovery race.
                const sessionChunks: Blob[] = [];
                let isSnapshotPersisting = false;
                let sessionSnapshotInFlight: Promise<unknown> | null = null;

                recorder.ondataavailable = async (event: BlobEvent) => {
                    // Set when the body throws, so the cross-tab hand-off waiter reports failure.
                    let handoffFailed = false;
                    // MediaRecorder fires this handler from native code; an unhandled rejection
                    // inside the async body becomes an unhandledRejection at the window level
                    // instead of reaching the user or our error path. Wrap the whole body so
                    // recoverIfPrefixed / localAudioDb / onAudioDataAvailable failures surface.
                    try {
                        // Only tear down the AudioContext at STOP. With a periodic timeslice this handler
                        // fires every PERSIST_SNAPSHOT_MS, so the close must wait for the inactive state.
                        if (recorder.state === "inactive" && audioContext && audioContext?.state !== "closed") {
                            audioContext?.close();
                        }

                        // Surfaces a persistence problem. `alertUser` tells the vet (used for
                        // mid-recording failures, where the IndexedDB snapshot is the ONLY durability
                        // — a swallow re-creates the Homestead loss). At stop the upload is the primary
                        // path and has its own error handling, so the local-copy failure there is
                        // telemetry-only to avoid false alarms.
                        const surfacePersistenceProblem = (errorId: string, alertUser: boolean) => {
                            if (alertUser) {
                                setRecordingMessage({
                                    message:
                                        "We're having trouble saving this recording. Keep this tab open and check your connection.",
                                    type: "error",
                                });
                            }
                            Sentry.captureException(new Error(errorId), {
                                tags: { errorCategory: "recording", errorId },
                                extra: buildRecordingErrorExtra({
                                    blobSize: event.data?.size,
                                    mimeType: event.data?.type,
                                    recordingTime,
                                    activeRecordingId,
                                    recordingState,
                                    mediaRecorderState: recorder.state,
                                    audioContextState: audioContext?.state,
                                    transcriptSectionId,
                                }),
                            });
                        };

                        // EN-836: persistence-only path (CHUNK_UPLOAD off — all production accounts).
                        // Handled BEFORE the size>0 gate so the terminal (stop) event still finalizes even
                        // when its blob is empty (a periodic tick may have just flushed the tail).
                        // MediaRecorder emits a chunk every PERSIST_SNAPSHOT_MS; we accumulate the raw
                        // chunks and re-assemble the full file each time (concatenating timeslice chunks
                        // reproduces the original valid container). Each interval we snapshot the growing
                        // file to IndexedDB as an INCOMPLETE upload (bytesUploaded=0), so a reload/crash
                        // mid-recording leaves a recoverable entry — picked up by the recovery effects in
                        // DataProvider (on startup and on network restore). Only at STOP do we run EN-719
                        // prefix recovery on the complete file and hand it to the single-file upload path —
                        // partial chunks are never uploaded. Re-serializing the whole file each tick is
                        // inherent to single-blob storage; we coalesce writes (isSnapshotPersisting) so a slow
                        // write can't pile up, and PERSIST_SNAPSHOT_MS bounds worst-case mid-recording loss.
                        if (!containsFeatureFlag(FeatureFlags.CHUNK_UPLOAD)) {
                            if (!patientRecord.transcriptions?.id || !patientRecord.id) return;
                            const isEnd = recorder.state === "inactive";

                            if (event.data.size > 0) {
                                sessionChunks.push(event.data);
                            }

                            if (!isEnd) {
                                // mid-recording snapshot — persist only, never upload. Skip if there's
                                // nothing new, or if a prior snapshot write is still in flight (coalesce).
                                if (event.data.size === 0 || isSnapshotPersisting) return;
                                const assembled = createAudioFromBlobs(sessionChunks);
                                if (!isFileSizeValid(assembled.size)) {
                                    surfacePersistenceProblem("recording.persist_snapshot_oversized", true);
                                    return;
                                }
                                isSnapshotPersisting = true;
                                const snapshotWrite = localAudioDb.addTranscriptSectionAudio(
                                    patientRecord.transcriptions.id,
                                    transcriptSectionId,
                                    assembled,
                                    assembled.name,
                                    0,
                                    patientRecord.id,
                                    patientRecord?.patient_name || "",
                                    patientRecord?.doctors?.id || "",
                                    undefined,
                                    recordingMonitoringDocumentIdRef?.current ?? null,
                                    accountDataRef.current?.id ?? null,
                                );
                                sessionSnapshotInFlight = snapshotWrite;
                                try {
                                    const persisted = await snapshotWrite;
                                    // addTranscriptSectionAudio returns false (never throws) on failure —
                                    // treating that as success would silently lose the recording.
                                    if (!persisted) {
                                        surfacePersistenceProblem("recording.persist_snapshot_failed", true);
                                    }
                                } finally {
                                    isSnapshotPersisting = false;
                                    if (sessionSnapshotInFlight === snapshotWrite) {
                                        sessionSnapshotInFlight = null;
                                    }
                                }
                                return;
                            }

                            // recording stopped — assemble the complete file from everything captured, run
                            // EN-719 prefix recovery, persist the final version, then upload once.
                            if (sessionChunks.length === 0) {
                                surfacePersistenceProblem("recording.no_audio_captured", true);
                                return;
                            }
                            // Wait for any in-flight mid snapshot to finish first, so its (unowned,
                            // bytesUploaded=0) write can't land after the claim below and re-open the
                            // recovery double-upload window. Its own handler surfaces errors.
                            if (sessionSnapshotInFlight) {
                                try {
                                    await sessionSnapshotInFlight;
                                } catch {
                                    // mid-snapshot failure is surfaced by the mid path; don't block stop
                                }
                            }

                            // Claim the existing (last mid-snapshot) row as an in-progress upload NOW,
                            // before the potentially slow recoverIfPrefixed. stopRecording already flipped
                            // the redux recording flag to inactive, so a network-restore recovery pass can
                            // reach retryIncompleteUploads ~250ms later (its cross-tab probe delay); if
                            // recoverIfPrefixed on a large file outlasts that, claiming only when the final
                            // file is persisted (below) would lose the race and let recovery upload this
                            // section's partial snapshot in parallel (EN-652). Claiming up front closes it —
                            // retryIncompleteUploads skips isUploading rows. No-op if no snapshot row exists
                            // yet (short recording); the final persist creates it claimed. The post-upload
                            // reconcile releases the claim if the upload never takes over.
                            await localAudioDb.updateUploadState(
                                patientRecord.transcriptions.id,
                                transcriptSectionId,
                                "",
                                undefined,
                                true,
                            );

                            const assembled = createAudioFromBlobs(sessionChunks);
                            const finalFile = await recoverIfPrefixed(
                                assembled,
                                "browser",
                                transcriptSectionId,
                                patientRecord.id,
                            );

                            // EN-1217: WebKit can reclaim the backing file of a Blob held across the
                            // recording session while leaving finalFile.size reporting the ORIGINAL byte
                            // count — every check below this point is size-based and would treat a hollow
                            // blob as a complete recording. Read it back before trusting it.
                            const integrity = await verifyAudioBlobIntegrity(finalFile);
                            if (integrity.kind === "dead") {
                                Sentry.captureException(new Error("recording.dead_blob_detected_at_stop"), {
                                    tags: {
                                        errorCategory: "recording",
                                        errorId: "recording.dead_blob_detected_at_stop",
                                    },
                                    extra: buildRecordingErrorExtra(
                                        buildDeadBlobSentryExtra(integrity, {
                                            blobSize: finalFile.size,
                                            mimeType: finalFile.type,
                                            transcriptSectionId,
                                            patientRecordId: patientRecord.id,
                                        }),
                                    ),
                                });
                                setRecordingMessage({
                                    message:
                                        "This recording's audio was lost by the browser and can't be recovered. Please re-record now.",
                                    type: "error",
                                });
                                // No recoverable bytes exist locally, including on any earlier mid-recording
                                // snapshot row for this section — it shares the same underlying Blob objects
                                // WebKit reclaimed. Delete rather than leave a row claimed (isUploading=true,
                                // set above) that retryIncompleteUploads would skip forever. Same no-op
                                // guarantee as the claim above applies here too: deleting a nonexistent key is
                                // a no-op, not an error, so this is safe even if this stop event never had a
                                // snapshot row to begin with.
                                await localAudioDb.deleteTranscriptSectionAudio(
                                    patientRecord.transcriptions.id,
                                    transcriptSectionId,
                                );
                                return;
                            }

                            if (isFileSizeValid(finalFile.size)) {
                                // isUploading=true: claim the row for the imminent single-file upload so a
                                // startup/network-restore recovery pass in the stop→upload window skips it
                                // (retryIncompleteUploads ignores isUploading rows). The upload path clears
                                // it — success makes the row complete; failure resets it so recovery retries.
                                const persisted = await localAudioDb.addTranscriptSectionAudio(
                                    patientRecord.transcriptions.id,
                                    transcriptSectionId,
                                    finalFile,
                                    finalFile.name,
                                    0,
                                    patientRecord.id,
                                    patientRecord?.patient_name || "",
                                    patientRecord?.doctors?.id || "",
                                    true,
                                    recordingMonitoringDocumentIdRef?.current ?? null,
                                    accountDataRef.current?.id ?? null,
                                );
                                if (!persisted) {
                                    surfacePersistenceProblem("recording.persist_final_failed", false);
                                }
                            } else {
                                surfacePersistenceProblem("recording.final_file_oversized", false);
                            }
                            await onAudioDataAvailable(
                                finalFile,
                                patientRecord,
                                transcriptSectionId,
                                blobIndexRef.current++,
                                true,
                            );

                            // Release the isUploading claim if the upload didn't take over. The production
                            // callback swallows upload errors (never throws), and some uploadAudio failure
                            // exits (e.g. a session/auth error before it starts) return without clearing the
                            // claim — leaving the row isUploading=true, which retryIncompleteUploads skips,
                            // stranding the recording as "processing" until a reload. onAudioDataAvailable has
                            // fully settled here (success → row complete; failure → early-returned), so if the
                            // row is still an incomplete, claimed upload, the upload failed: clear the claim so
                            // recovery can retry it within this session.
                            try {
                                const row = await localAudioDb.getTranscriptSectionAudio(
                                    patientRecord.transcriptions.id,
                                    transcriptSectionId,
                                );
                                if (row && row.isUploading && row.bytesUploaded < row.audioData.size) {
                                    await localAudioDb.updateUploadState(
                                        patientRecord.transcriptions.id,
                                        transcriptSectionId,
                                        row.uploadUrl || "",
                                        row.bytesUploaded,
                                        false,
                                        row.retryCount,
                                    );
                                }
                            } catch (reconcileError) {
                                Sentry.captureException(reconcileError, {
                                    tags: {
                                        errorCategory: "recording",
                                        errorId: "recording.stop_claim_reconcile_failed",
                                    },
                                });
                            }
                            return;
                        }

                        if (event.data.size > 0) {
                            if (!patientRecord.transcriptions?.id || !patientRecord.id) return;

                            // CHUNK_UPLOAD path only — the CHUNK_UPLOAD-off (production) path returned
                            // above. Continuation chunks are raw Cluster data with no EBML magic, so we
                            // do NOT run per-chunk prefix recovery here (EN-719 recovery runs on the
                            // assembled file in the persistence-only path above). Pass closure-stable ids:
                            // this awaits below and stopRecording's cleanup may null the refs mid-await.
                            const audioFile = createAudioFile(event.data);

                            // append to in-mem chunk-upload accumulator buffer
                            chunkBuffer.current.push({
                                transcriptSectionId,
                                transcriptionId: patientRecord.transcriptions.id,
                                patientRecordId: patientRecord.id,
                                chunkIndex: blobIndexRef.current,
                                success: false,
                                uploading: false,
                                data: audioFile,
                                retryCount: 0,
                            });

                            const end = recorder.state === "inactive";

                            // handle audio data
                            await onAudioDataAvailable(
                                audioFile,
                                patientRecord,
                                transcriptSectionId,
                                blobIndexRef.current++,
                                end,
                            );

                            // if this is the end of the recording, let's handle incomplete
                            // we do this here instead of in stopRecording() because we want to handle the last chunk
                            if (end) {
                                _handleIncomplete(patientRecord);
                            }
                        }
                    } catch (err) {
                        handoffFailed = true;
                        setRecordingMessage({
                            message: "An error occurred while saving the recording.",
                            type: "error",
                        });
                        Sentry.captureException(err, {
                            tags: { errorCategory: "recording", errorId: "recording.browser_ondataavailable_failed" },
                            // Use closure-stable transcriptSectionId (NOT the ref): ondataavailable
                            // fires from mediaRecorder.stop() inside stopRecording, then awaits inside
                            // the try body. By the time the catch runs, stopRecording's cleanup has
                            // nulled transcriptSectionIdRef — same hazard the comment at L1233-1237
                            // describes for the success path.
                            extra: buildRecordingErrorExtra({
                                blobSize: event.data?.size,
                                mimeType: event.data?.type,
                                recordingTime,
                                activeRecordingId,
                                recordingState,
                                mediaRecorderState: recorder.state,
                                audioContextState: audioContext?.state,
                                transcriptSectionId,
                            }),
                        });
                    } finally {
                        // EN-837: the terminal stop event owns the `finalizing` bridge (armed in
                        // stopRecording's browser branch before the synchronous `inactive` dispatch).
                        // Release it here — on the terminal event ONLY — no matter how the handoff ended:
                        // upload started (already cleared in uploadAudio, so this is a no-op), upload
                        // bailed before starting (no session / auth error — the prod callback swallows and
                        // never clears it), the handler early-returned (no audio captured), or the body
                        // threw. Without this, a lingering `finalizing` keeps the recorder "busy" until the
                        // 120s watchdog, delaying deferred teardowns (sign-out, router swap) by ~2min. This
                        // runs AFTER `await onAudioDataAvailable`, so on success `isUploadingGlobal` already
                        // owns "busy" — no false-idle gap. Mid-recording snapshot ticks (state==="recording")
                        // are skipped: `finalizing` is only ever set at stop. Keyed by the closure
                        // `transcriptSectionId` (== the arm-site id); settling an unarmed key is a no-op.
                        if (recorder.state === "inactive") {
                            dispatch(finalizingSettled(transcriptSectionId));
                            settleHandoff(transcriptSectionId, !handoffFailed);
                        }
                    }
                };
            })
            .catch((err: unknown) => {
                if (err instanceof MicStartError) throw err;
                // The mic opened but recorder setup failed (for example MediaRecorder rejected the mime type).
                // Release the mic so a setup failure never leaves it open.
                openedStream?.getTracks().forEach((t) => t.stop());
                // In the usual setup failure (a rejected mime type) the recorder never started, so its
                // terminal handler will not fire and nothing else closes the context: close it here and
                // drop the live ref that holds it.
                if (openedContext && openedContext.state !== "closed") {
                    void Promise.resolve(openedContext.close()).catch(() => undefined);
                }
                if (openedContext && liveBrowserRecorderRef.current?.audioContext === openedContext) {
                    liveBrowserRecorderRef.current = null;
                }
                setAudioStream(undefined);
                // The dead recorder must not linger as `mediaRecorder`: Stop falls back to it once the
                // live ref is null, and stop() on an inactive recorder throws before the refs clear.
                setMediaRecorder(undefined);
                setRecordingMessage({ message: "Error setting up microphone.", type: "error" });
                setRecordingState(RecordingState.no_permission);
                dispatch(setGlobalRecordingState(RecordingState.no_permission));
                // The lease is taken only after recorder.start(), so for a pre-start failure this is a
                // no-op safety net. An hourly rollover keeps it: the last hour is still finalizing and
                // the vet's Stop reads it.
                if (!options?.reuseSessionAudio) {
                    setRecordingVisit(null);
                    activeTransportRef.current = null;
                    patientRecordRef.current = null;
                    transcriptSectionIdRef.current = null;
                    onAudioDataAvailableRef.current = null;
                    // A fresh start must reject so the caller does not show RECORDING while nothing records.
                    throw new Error("Error setting up microphone.");
                }
            });
    }

    // EN-719: validate every WebM blob right before upload. WebM uploads with stale Matroska
    // bytes prepended (from a prior recording's tail) were causing AssemblyAI to reject
    // the file with "does not appear to contain audio". We strip the prefix client-side
    // AND report to Sentry so we can see the underlying race fire in prod even after the
    // chunk accumulator fix.
    //
    // `accountData` and `isExtensionAvailableInProvider` are read through refs that are
    // updated every render (see accountDataRef / isExtensionAvailableInProviderRef above)
    // so the Sentry event always tags the values current at invocation time, not the
    // values from the render that bound this closure. The other captures (refs and
    // functions like `setRecordingMessage`) are stable identities and don't need this.
    // `explicitTranscriptSectionId` / `explicitPatientRecordId` let callers pass the ids of
    // the recording actually being validated, instead of trusting the refs. Necessary in
    // two paths:
    //   1. Hourly rollover — `transcriptSectionIdRef.current` is reassigned to the NEXT
    //      session's uuid BEFORE recoverIfPrefixed runs on the just-stopped session's blob.
    //   2. Browser-transport `mediaRecorder.ondataavailable` — fires synchronously from
    //      `mediaRecorder.stop()` inside stopRecording, then awaits the blob-header read.
    //      During that await, stopRecording's own cleanup nulls patientRecordRef and
    //      transcriptSectionIdRef, so the ref reads would tag Sentry with null ids.
    // Without these overrides the Sentry event tags the wrong recording (or no recording),
    // making forensic correlation impossible.
    async function recoverIfPrefixed(
        file: File,
        transport: "extension" | "browser",
        explicitTranscriptSectionId?: string,
        explicitPatientRecordId?: string,
    ): Promise<File> {
        // MediaRecorder falls back to audio/mp3 / audio/wav / audio/mp4 when audio/webm isn't
        // supported (Safari, older iOS). Those formats use different container magic (RIFF,
        // ftyp box) and the prefix-leak mechanism is WebM-specific. Validating them with the
        // EBML check would always fail and would flood Sentry with false positives.
        if (!file.type.startsWith("audio/webm")) return file;

        // Read ONLY the head needed for validation — never the whole blob. Hour-long
        // recordings can be 100+ MB; loading them into the JS heap solely to check 64 KiB
        // could OOM the tab on memory-constrained devices (tablets, older laptops).
        let head: Uint8Array;
        try {
            head = new Uint8Array(await file.slice(0, WEBM_HEADER_SCAN_BYTES).arrayBuffer());
        } catch (err) {
            Sentry.captureException(err, {
                tags: {
                    errorCategory: "recording",
                    errorId: "webm.upload.read_failed",
                    "webm.transport": transport,
                },
                extra: buildRecordingErrorExtra({
                    blobSize: file.size,
                    mimeType: file.type,
                    transcriptSectionId: explicitTranscriptSectionId ?? transcriptSectionIdRef.current,
                }),
            });
            return file;
        }

        const result = validateWebmHeader(head);
        if (result.kind === "valid") return file;

        const accState = chunkAccumulatorRef.current.state();
        const currentAccountData = accountDataRef.current;
        const sharedTags = {
            errorCategory: "recording",
            "webm.transport": transport,
            "webm.activeTransport": activeTransportRef.current ?? "null",
            "webm.extensionInstalled": String(isExtensionAvailableInProviderRef.current),
            "webm.mimeType": file.type || "unknown",
        };
        const sharedExtra = buildRecordingErrorExtra({
            blobSize: file.size,
            firstBytesHex: firstBytesHex(head, 64),
            accumulatorChunkCount: accState.chunkCount,
            accumulatorDroppedDisarmed: accState.droppedDisarmed,
            accumulatorDroppedWrongSession: accState.droppedWrongSession,
            patientRecordId: explicitPatientRecordId ?? patientRecordRef.current?.id,
            transcriptSectionId: explicitTranscriptSectionId ?? transcriptSectionIdRef.current,
            accountId: currentAccountData && "id" in currentAccountData ? currentAccountData.id : undefined,
            userAgent: typeof navigator !== "undefined" ? navigator.userAgent : "unknown",
        });

        if (result.kind === "invalid") {
            // Not a WebM at all (or magic past 64 KiB). We still let the upload through so the
            // server has the bytes for forensics, but surface an immediate message so the user
            // isn't blindsided by a "transcription failed" hours later.
            Sentry.captureException(new Error("webm.upload.unrecoverable_header"), {
                tags: { ...sharedTags, errorId: "webm.upload.unrecoverable_header" },
                extra: sharedExtra,
            });
            setRecordingMessage({
                message: "This recording appears corrupted. Please re-record this clip.",
                type: "error",
            });
            return file;
        }

        // Recoverable: strip using file.slice (returns a Blob view — no full read into the heap).
        const recoveredBlob = file.slice(result.prefixBytes);
        const recovered = new File([recoveredBlob], file.name, { type: file.type });
        // Recovered events are warning-level: the user's recording is preserved, so this
        // is not an outage. But we DO want a Sentry Issue because firing at all signals
        // a chunk-accumulator regression worth investigating.
        Sentry.captureException(new Error("webm.upload.prefix_corruption_detected"), {
            level: "warning",
            tags: { ...sharedTags, errorId: "webm.upload.prefix_corruption_detected", "webm.recovered": "true" },
            extra: {
                ...sharedExtra,
                prefixBytes: result.prefixBytes,
                prefixHex: result.prefixHex,
                recoveredSize: recovered.size,
            },
        });
        return recovered;
    }

    /**
     * EN-1609: the extension's start replaced a paused capture no live tab owned (a live owner is asked to
     * stop and save first). Its audio may be the only copy, so save it as an abandoned row: recovery
     * uploads it on its next pass and LocalStorageModal lists it meanwhile. Without its visit's ids the
     * row can't upload, but it is kept locally for manual retry. Never throws.
     */
    async function saveReplacedPausedSession(replaced: ReplacedPausedSession | undefined): Promise<void> {
        if (!replaced) return;
        const { data, visitId, transcriptSectionId } = replaced;
        let saved = false;
        let linked = false;
        try {
            if (data) {
                // A failed lookup still saves the audio, just without its visit.
                const visit = visitId
                    ? await fetchPatientRecord(visitId).then(
                          (r) => r.data,
                          () => null,
                      )
                    : null;
                const transcriptId = visit?.transcriptions?.id ?? "";
                linked = !!(visit?.id && transcriptId && transcriptSectionId);
                // Unlinked, the known ids are still kept so the row can be matched to its visit by hand.
                const target = {
                    transcriptId: linked ? transcriptId : "",
                    transcriptSectionId: transcriptSectionId ?? uuidv4(),
                    patientRecordId: visitId ?? "",
                    patientName: linked ? visit?.patient_name || "" : "Recovered extension recording",
                    doctorId: linked ? visit?.doctors?.id || "" : "",
                };
                const type = data.match(/^data:([^;,]+)/)?.[1] ?? "audio/webm";
                const file = createAudioFile(assembleChunksToBlob([data], type));
                saved = await localAudioDb.addTranscriptSectionAudio(
                    target.transcriptId,
                    target.transcriptSectionId,
                    file,
                    file.name,
                    0,
                    target.patientRecordId,
                    target.patientName,
                    target.doctorId,
                    false,
                    null,
                    accountDataRef.current?.id ?? null,
                    {
                        ownerTabNonce: ORPHANED_EXTENSION_OWNER,
                        snapshotAt: new Date(Date.now() - STALE_OWNER_MS),
                        // Without a transcript id it can't upload: keep it for manual retry only.
                        ...(linked ? {} : { manualRetryOnly: true }),
                    },
                );
            }
        } catch {
            saved = false; // reported below
        }
        Sentry.captureMessage("Extension replaced an orphaned paused capture", {
            level: "warning",
            tags: { errorCategory: "recording", errorId: "recording.extension_orphaned_paused_session_replaced" },
            extra: { visitId, saved, linked },
        });
    }

    /**
     * EN-1609: start extension capture, saving any orphaned paused session the extension hands back,
     * even when the start itself then fails (the provider attaches the extension's response to the
     * rejection as `data`). A paused session too large to hand back is left in place and reported.
     */
    async function startExtensionCapture(config: AudioCaptureConfig): Promise<ExtensionStartResult | undefined> {
        const handleOutcome = (outcome: ExtensionStartResult | undefined) => {
            void saveReplacedPausedSession(outcome?.replacedPausedSession);
            if (outcome?.orphanTooLarge) {
                // The extension can't hand it back, and nothing here can stop it without losing it.
                toast.error(
                    "A recording from a closed HappyDoc panel is still holding the microphone. Reload the HappyDoc extension to continue.",
                );
                Sentry.captureMessage("Extension paused capture too large to replace", {
                    level: "warning",
                    tags: {
                        errorCategory: "recording",
                        errorId: "recording.extension_orphaned_paused_session_too_large",
                    },
                });
            }
        };
        let result: ExtensionStartResult | undefined;
        try {
            result = await startAudioCapture(config);
        } catch (err) {
            handleOutcome((err as { data?: ExtensionStartResult } | null)?.data);
            throw err;
        }
        handleOutcome(result);
        return result;
    }

    /**
     * Creates a file from the audioBlob
     */
    function createAudioFile(audioBlob: Blob): File {
        const timeNow = new Date().toISOString();
        const mimeType = audioBlob.type;
        const audioName = `audio-${timeNow}-${Math.floor(Math.random() * 1000)}.${mimeType.split("/")[1].split(";")[0]}`;

        return new File([audioBlob], audioName, { type: mimeType });
    }

    function createAudioFromBlobs(audioBlobs: Blob[]): File {
        const timeNow = new Date().toISOString();
        const mimeType = audioBlobs[0].type;
        const audioName = `audio-${timeNow}-${Math.floor(Math.random() * 1000)}.${mimeType.split("/")[1].split(";")[0]}`;

        return new File(audioBlobs, audioName, { type: mimeType });
    }

    /**
     * Calling this method would pause the recording if it is currently running or resume if it is paused. Toggles the value `isPaused`
     */
    const togglePauseResume: () => Promise<void> = useCallback(async () => {
        if (isRecordingError(recordingState)) {
            throw new Error(`Cannot pause/resume recording in error state "${recordingState}"`);
        }

        // Race any extension postMessage round-trip against a hard deadline. sendToParent
        // has its own 8s timeout, but this acts as a hook-level safety net so a future
        // provider change can't silently remove that guarantee.
        const withTimeout = <T,>(p: Promise<T>, ms: number, label: string): Promise<T> =>
            Promise.race([
                p,
                new Promise<T>((_, reject) =>
                    setTimeout(() => reject(new Error(`${label} timed out after ${ms}ms`)), ms),
                ),
            ]);

        if (recordingState === RecordingState.paused) {
            Sentry.addBreadcrumb({
                category: "recording",
                message: "recording.media_recorder_resume",
                level: "info",
                data: {
                    recordingTime: recordingTimeRef.current,
                    activeRecordingId: activeRecordingIdRef.current,
                    transcriptSectionId: transcriptSectionIdRef.current,
                    mediaRecorderState: mediaRecorder?.state,
                    transport: activeTransportRef.current,
                },
            });
            if (activeTransportRef.current === "extension") {
                // State must follow the async call so a failure leaves the UI in the correct
                // paused state rather than incorrectly showing 'recording'.
                try {
                    await withTimeout(resumeAudioCapture(), 8000, "resumeAudioCapture");
                    setRecordingState(RecordingState.recording);
                    _startTimer();
                } catch (err) {
                    Sentry.captureException(err instanceof Error ? err : new Error(String(err)), {
                        tags: { errorCategory: "recording", errorId: "recording.resume_extension_failed" },
                    });
                    setRecordingMessage({ message: "Failed to resume recording. Please try again.", type: "error" });
                }
            } else {
                setRecordingState(RecordingState.recording);
                mediaRecorder?.resume();
                _startTimer();
            }
        } else {
            Sentry.addBreadcrumb({
                category: "recording",
                message: "recording.media_recorder_pause",
                level: "info",
                data: {
                    recordingTime: recordingTimeRef.current,
                    activeRecordingId: activeRecordingIdRef.current,
                    transcriptSectionId: transcriptSectionIdRef.current,
                    mediaRecorderState: mediaRecorder?.state,
                    transport: activeTransportRef.current,
                },
            });
            if (activeTransportRef.current === "extension") {
                // State must follow the async call so a failure leaves the UI in the correct
                // recording state rather than incorrectly showing 'paused' with a stopped timer.
                try {
                    await withTimeout(pauseAudioCapture(), 8000, "pauseAudioCapture");
                    setRecordingState(RecordingState.paused);
                    _stopTimer();
                } catch (err) {
                    Sentry.captureException(err instanceof Error ? err : new Error(String(err)), {
                        tags: { errorCategory: "recording", errorId: "recording.pause_extension_failed" },
                    });
                    setRecordingMessage({ message: "Failed to pause recording. Please try again.", type: "error" });
                }
            } else {
                setRecordingState(RecordingState.paused);
                _stopTimer();
                // EN-1609: flush audio recorded since the last timeslice to the IndexedDB snapshot
                // before pausing, so a paused session's latest audio is persisted.
                if (mediaRecorder?.state === "recording") mediaRecorder.requestData();
                mediaRecorder?.pause();
            }
        }
    }, [
        mediaRecorder,
        recordingState,
        setRecordingState,
        _startTimer,
        _stopTimer,
        pauseAudioCapture,
        resumeAudioCapture,
        setRecordingMessage,
    ]);

    return {
        startRecording,
        stopRecording,
        togglePauseResume,
        isRecording: isRecording(recordingState),
        recordingState,
        recordingTime,
        audioStream,
        activeRecordingId,
        setActiveRecordingId,
        resetRecorder,
        setRecordingState,
        recordingMessage,
        setRecordingMessage,
        chunkBuffer,
        blobIndexRef,
        pauseResumeSupported,
        // Same predicate resetRecorder uses: the locked transport, else what a new start would pick.
        isExtensionTransport:
            activeTransportRef.current === "extension" || (activeTransportRef.current === null && isExtensionActive),
    };
};

export default useAudioRecorder;
